<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDateField, formatDocDate } from '@/shared/composables/dateUtils';
import { useAddressFormatter } from '@/shared/composables/useAddressFormatter';
import { useEntityDocVariables } from '@/shared/composables/useEntityDocVariables';
import { useOfficerAppointmentStore } from '../stores/officerAppointmentStore';
import { getOfficersByEntity, getOfficers, getDirectorsByEntityAlternate } from '../api/officerAppointmentApi';
import ContactListPage from '@/features/Contact/views/ContactListPage.vue';
import ContactDetailPage from '@/features/Contact/views/ContactDetailPage.vue';
import TaskFlowSection from './TaskFlowSection.vue';
import type { TaskConfig } from '../api/taskConfigApi';
import { useBaseCode, baseCodesToOptions } from '@/shared/composables/useBaseCode';
import type { BaseCode } from '@/shared/types/baseCode.types';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useDialog } from '@/shared/composables/useDialog';
import { entityApi } from '@/features/Entity/api/entityApi';
import { isDevelopment, isLocal } from '@/shared/config/env';
import { apiClient } from '@/shared/api/client';
import { buildOfficerAppointmentContactDataFromFlat } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/adaptOfficerAppointmentEfilingFormData';
import { splitContactDisplayName } from '@/features/startTask/utils/manualOfficerContactData';

const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const { confirm } = useDialog();

// 주소 포맷팅 함수
const { formatAddress } = useAddressFormatter();

// Entity 데이터 조회 함수
const { getEntityData } = useEntityDocVariables();

// 아코디언 상태 관리
const expandedPanels = ref<number[]>([0]); // 첫 번째 패널 기본 열림

// Expand All / Collapse All
const expandAll = () => {
  expandedPanels.value = [0]; // 현재는 단일 항목이므로 0번만
};

const collapseAll = () => {
  expandedPanels.value = [];
};

const isAllExpanded = computed(() => expandedPanels.value.length > 0);

// Props: CreateTaskForm에서 선택한 taskConfig 전달받기
interface Props {
  taskConfig?: TaskConfig | null;
  initialData?: any | null; // E-Filing 페이지에서 TASK_DATA 전달용
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 검증용)
  showTaskFlow?: boolean; // Task Flow 섹션 표시 여부 (E-Filing에서는 false)
  eFilingMode?: boolean; // E-Filing 모드 (Entity, Position, Date, Officer 필드 disabled + Contact 필드 필수 표시)
  validationErrors?: Record<string, string>; // 필드별 validation 에러 메시지
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  initialData: null,
  readOnly: false,
  showTaskFlow: true,
  eFilingMode: false,
  validationErrors: () => ({})
});

// E-Filing 모드 여부 (템플릿에서 쉽게 사용하기 위한 computed)
const eFilingMode = computed(() => props.eFilingMode);

// Store
const officerAppointmentStore = useOfficerAppointmentStore();

// BaseCode composable
const { getCodesByMainCode, loading: baseCodeLoading } = useBaseCode();

// Emits
const emit = defineEmits<{
  'auto-select-templates': [config: { categoryName: string; templateIds: string[] }],
  'filter-select-templates': [config: { categoryName: string; templateIds: string[]; filterValue: string }],
  'reset-templates': [],
  'update:taskFlow': [value: { all: boolean; createDocument: boolean; requestTSign: boolean; proceedWithEFiling: boolean }]
}>();

// Task Flow 상태
const taskFlow = ref({
  all: false,
  createDocument: false,
  requestTSign: false,
  proceedWithEFiling: false
});

// Position 필드 (computed에서 사용하기 위해 먼저 선언)
const position = ref('');

// Position 코드 목록 (BaseCode에서 로드됨) - computed에서 사용하기 위해 먼저 선언
const positionCodes = ref<BaseCode[]>([]);

// Task Flow 지원 여부 (PROCESS_STEPS 기반) - 대소문자 구분 없이 비교
const isCreateDocumentSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some(step => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'doc' || lowerStep === 'document' || lowerStep.includes('doc');
  });
});

const isRequestTSignSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some(step => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 't-sign' || lowerStep === 'tsign' || lowerStep.includes('sign');
  });
});

const isProceedWithEFilingSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some(step => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'e-filing' || lowerStep.includes('filing');
  });
});


// Director, Secretary, Auditor, CEO, Managing Director, Alternate Director인 경우 E-Filing 활성화 (모든 환경)
const isEFilingEnabledForPosition = computed(() => {
  const positionCode = position.value;
  if (!positionCode) return false;

  // positionCodes에서 선택된 position의 codeName을 찾아서 비교
  const selectedCode = positionCodes.value.find(code => code.totalCode === positionCode);
  const positionName = selectedCode?.codeName?.toLowerCase() || '';

  // Director, Secretary, Auditor(B30025), CEO, Managing Director, Alternate Director(B30024)인 경우 E-Filing 활성화 (모든 환경)
  return positionName === 'director' || 
         positionName === 'secretary' || 
         positionName === 'auditor' || 
         positionCode === 'B30025' ||
         positionName === 'chief executive officer' || 
         positionName === 'managing director' ||
         positionCode === 'B30024'; // Alternate Director
});

// 실제 E-Filing 지원 여부 (processSteps 지원 + Position이 Director/Secretary/Auditor/CEO/Managing Director/Alternate Director)
const isEFilingSupportedFinal = computed(() => {
  return isProceedWithEFilingSupported.value && isEFilingEnabledForPosition.value;
});

// 실제 All 지원 여부
const isAllSupportedFinal = computed(() => {
  return isCreateDocumentSupported.value &&
         isRequestTSignSupported.value &&
         isEFilingSupportedFinal.value;
});

// Task Flow 변경 시 상위 컴포넌트에 전달
watch(taskFlow, (newValue) => {
  emit('update:taskFlow', { ...newValue });
}, { deep: true });

// Task Flow 상태 업데이트 함수
const updateTaskFlowState = () => {
  taskFlow.value.createDocument = isCreateDocumentSupported.value;
  taskFlow.value.requestTSign = isRequestTSignSupported.value;
  taskFlow.value.proceedWithEFiling = isEFilingSupportedFinal.value;
  taskFlow.value.all = isAllSupportedFinal.value;
};

// taskConfig 변경 시 Task Flow 자동 업데이트
watch(() => props.taskConfig, () => {
  updateTaskFlowState();
}, { immediate: true });

// Position 로딩 상태 (positionCodes는 위에서 이미 선언됨)
const isLoadingPositions = ref(false);

// Position 정렬 순서 정의 (codeName 기준 - 대소문자 무시)
const positionOrderByName: Record<string, number> = {
  'director': 1,
  'secretary': 2,
  'managing director': 3,
  'chief executive officer': 4,
};

// Position 옵션 (v-select용) - 정렬
const positionOptions = computed(() => {
  // 모든 Position 코드를 정렬된 옵션 목록으로 변환
  const sortedOptions = [...positionCodes.value]
    .sort((a, b) => {
      const nameA = a.codeName.toLowerCase();
      const nameB = b.codeName.toLowerCase();
      const orderA = positionOrderByName[nameA] ?? 999;
      const orderB = positionOrderByName[nameB] ?? 999;
      return orderA - orderB;
    })
    .map(code => ({
      title: code.codeName,
      value: code.totalCode,
      props: { disabled: false }
    }));

  return sortedOptions;
});

// Position 코드 로드 함수
const loadPositionCodes = async () => {
  try {
    isLoadingPositions.value = true;
    // country_code='SG', main_code='B30' 조회
    const codes = await getCodesByMainCode('SG', 'B30');
    // bigo2='A04001'인 값만 필터링
    let filteredCodes = codes.filter(code => code.bigo2 === 'A04001');
    
    positionCodes.value = filteredCodes;
  } catch (error) {
    console.error('Failed to load position codes:', error);
    positionCodes.value = [];
  } finally {
    isLoadingPositions.value = false;
  }
};

// 전체 Officer 목록 (초기 로드)
const allOfficers = ref<Array<{ contactCd: string; contactName: string; categoryCd?: string }>>([]);
const isLoadingAllOfficers = ref(false);

// 현재 선택된 Entity와 Position에 해당하는 Officer 목록 (제외할 대상)
const excludedOfficers = ref<Array<{ contactCd: string; contactName: string; appointmentDate?: string }>>([]);
const isLoadingOfficers = ref(false);

// Officer 옵션 (v-select용) - 전체 목록에서 제외된 대상 제외
// AUDITOR(B30025) 선택 시 CATEGORY_CD가 'B09001'인 Contact만 표시
// AUDITOR를 제외한 직책 선택 시 CATEGORY_CD가 'B09002'인 Contact만 표시
const officerOptions = computed(() => {
  const excludedContactCds = excludedOfficers.value.map(o => o.contactCd);
  let filteredOfficers = allOfficers.value.filter(officer => !excludedContactCds.includes(officer.contactCd));
  
  // Position이 AUDITOR(B30025)인 경우 CATEGORY_CD가 'B09001'인 Contact만 필터링 (회사)
  if (position.value === 'B30025') {
    filteredOfficers = filteredOfficers.filter(officer => officer.categoryCd === 'B09001');
  } else if (position.value) {
    // AUDITOR를 제외한 다른 직책인 경우 CATEGORY_CD가 'B09002'인 Contact만 필터링 (개인)
    filteredOfficers = filteredOfficers.filter(officer => officer.categoryCd === 'B09002');
  }
  
  const base = filteredOfficers.map((officer) => ({
    title: officer.contactName,
    value: officer.contactCd,
  }));
  if (isManualOfficerSentinel(officerName.value)) {
    const manualTitle = String(contactData.value.CONTACT_NAME || '').trim();
    if (manualTitle && !base.some((o) => o.value === MANUAL_OFFICER_SENTINEL)) {
      return [{ title: manualTitle, value: MANUAL_OFFICER_SENTINEL }, ...base];
    }
  }
  return base;
});

// 전체 Officer 목록 조회 함수
const loadAllOfficers = async () => {
  try {
    isLoadingAllOfficers.value = true;
    const response = await getOfficers();
    if (response.success && response.data) {
      const officers = response.data.officers || [];
      
      // 각 Officer의 categoryCd 조회 (AUDITOR 필터링을 위해 필요)
      const officersWithCategory = await Promise.all(
        officers.map(async (officer: any) => {
          // API 응답에 이미 categoryCd가 있으면 사용
          if (officer.categoryCd || officer.CATEGORY_CD) {
            return {
              contactCd: officer.contactCd || officer.CONTACT_CD || '',
              contactName: officer.contactName || officer.CONTACT_NAME || '',
              categoryCd: officer.categoryCd || officer.CATEGORY_CD || ''
            };
          }
          
          // categoryCd가 없으면 Contact 상세 정보 조회
          try {
            const contactResponse = await contactsApi.getContactById(officer.contactCd || officer.CONTACT_CD);
            const contact = (contactResponse as any).contact || contactResponse.data?.contact;
            return {
              contactCd: officer.contactCd || officer.CONTACT_CD || '',
              contactName: officer.contactName || officer.CONTACT_NAME || '',
              categoryCd: contact?.CATEGORY_CD || contact?.categoryCd || ''
            };
          } catch (error) {
            console.warn('Failed to fetch categoryCd for', officer.contactCd || officer.CONTACT_CD, error);
            return {
              contactCd: officer.contactCd || officer.CONTACT_CD || '',
              contactName: officer.contactName || officer.CONTACT_NAME || '',
              categoryCd: ''
            };
          }
        })
      );
      
      allOfficers.value = officersWithCategory;
    } else {
      allOfficers.value = [];
    }
  } catch (error: any) {
    console.error('Failed to fetch all officers:', error);
    allOfficers.value = [];
  } finally {
    isLoadingAllOfficers.value = false;
  }
};

// 현재 선택된 Entity와 Position에 해당하는 Officer 목록 조회 (제외할 대상)
const loadOfficers = async (entityCd: string, positionCd: string) => {
  if (!entityCd || !entityCd.trim() || !positionCd || !positionCd.trim()) {
    excludedOfficers.value = [];
    return;
  }

  try {
    isLoadingOfficers.value = true;
    const response = await getOfficersByEntity(entityCd, positionCd);
    if (response.success && response.data) {
      excludedOfficers.value = response.data.officers || [];
    } else {
      excludedOfficers.value = [];
    }
  } catch (error: any) {
    console.error('Failed to fetch excluded officers:', error);
    excludedOfficers.value = [];
  } finally {
    isLoadingOfficers.value = false;
  }
};

// Contact 목록 모달 상태
const showContactListModal = ref(false);
const contactListInitialSearch = ref('');

// Contact 상세 화면 모달 상태
const showContactDetailModal = ref(false);

// Contact 데이터 (Basic, Residential Address, Contact 섹션용)
const contactData = ref<any>({
  // Basic 섹션
  CONTACT_NAME: '',
  FIRST_NAME: '',
  MIDDLE_NAME: '',
  SURNAME: '',
  ID_TYPE_CD: '',
  CONTACT_REG_NO: '',
  NATIONALITY_CD: '',
  DATE_OF_BIRTH: '',
  CATEGORY_CD: '', // E-Filing officerCategory용 (B09001: Corporate, B09002: Individual)
  // Corporate 필드 (CATEGORY_CD = 'B09001')
  JURISDICTION: '',
  AUDITOR_YN: 'No', // 'Yes' or 'No'
  DATE_OF_INCORPORATION: '',
  REPRESENTATIVE_CD: '',
  // Residential Address 섹션
  ADDRESS_TYPE_CD: 'B07001',
  POSTAL_CODE: '',
  BLOCK_NO: '',
  STREET_NAME: '',
  BUILDING_NAME: '',
  ADDR_LEVEL: '',
  ADDR_UNIT: '',
  FOREIGN_ADDRESS_LINE_1: '',
  FOREIGN_ADDRESS_LINE_2: '',
  // Contact Address 섹션 (대체 주소)
  ALTERNATE_ADDRESS_CD: '',
  ALT_POSTAL_CODE: '',
  ALT_BLOCK_NO: '',
  ALT_STREET_NAME: '',
  ALT_BUILDING_NAME: '',
  ALT_ADDR_LEVEL: '',
  ALT_ADDR_UNIT: '',
  ALT_FOREIGN_ADDRESS_LINE_1: '',
  ALT_FOREIGN_ADDRESS_LINE_2: '',
  // Contact 섹션
  COUNTRY_CD: '',
  AREA_CODE: '',
  MOBILE_NO: '',
  CONTACT_EMAIL: ''
});

// 회사(Corporate) 여부 확인
const isCorporate = computed(() => {
  return contactData.value.CATEGORY_CD === 'B09001';
});

// 개인(Individual) 여부 확인
const isIndividual = computed(() => {
  return contactData.value.CATEGORY_CD === 'B09002';
});

/** GUID 연결 없이 FORM_DATA 로 채운 이사 상세도 표시 */
const hasOfficerDetailPanel = computed(() => {
  if (isManualOfficerSentinel(officerName.value)) return true;
  if (String(officerName.value || '').trim()) return true;
  return !!String(contactData.value.CONTACT_NAME || '').trim();
});

// BaseCode 옵션 데이터 - bigo1은 E-Filing용 name
const identificationTypeOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const nationalityOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const countryOptions = ref<Array<{ title: string; value: string; bigo3?: string }>>([]);
const addressTypeOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const companyTypeOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]); // E-Filing entityType용 (A04) - bigo1은 E-Filing name용
const contactCategoryOptions = ref<Array<{ title: string; value: string; bigo3?: string }>>([]); // E-Filing officerCategory용 (B09) - bigo3는 E-Filing name용
// Jurisdiction 옵션 (AUDITOR용)
const jurisdictionOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const representativeOptions = ref<Array<{ CONTACT_CD: string; CONTACT_NAME: string }>>([]);

// Alternate Director 관련 상태
const isAlternateDirector = computed(() => position.value === 'B30024');
const directorOptions = ref<Array<{ title: string; value: string }>>([]);
const isLoadingDirectors = ref(false);
const alternateDirectorData = ref({
  directorCd: '',
  startDate: '',
  endDate: '',
  hasEndDate: false // endDate가 있는지 여부 (true/false)
});

// Start date 필드 composable
const {
  dateValue: alternateDirectorStartDateValue,
  dateDisplay: alternateDirectorStartDateDisplayRaw,
  datePickerValue: alternateDirectorStartDatePickerValue,
  dateMenu: alternateDirectorStartDateMenu,
} = useDateField();

// Start date 표시용 포맷 (10 Dec 2025 형식) - 세션의 dateFormat 사용
const alternateDirectorStartDateDisplay = computed(() => {
  const dateVal = alternateDirectorStartDateValue.value;
  return formatDocDate(dateVal);
});

// End date 필드 composable
const {
  dateValue: alternateDirectorEndDateValue,
  dateDisplay: alternateDirectorEndDateDisplayRaw,
  datePickerValue: alternateDirectorEndDatePickerValue,
  dateMenu: alternateDirectorEndDateMenu,
} = useDateField();

// End date 표시용 포맷 (10 Dec 2025 형식) - 세션의 dateFormat 사용
const alternateDirectorEndDateDisplay = computed(() => {
  const dateVal = alternateDirectorEndDateValue.value;
  return formatDocDate(dateVal);
});

// Start date 변경 시 alternateDirectorData 업데이트
watch(alternateDirectorStartDateValue, (newValue) => {
  if (props.readOnly) return;
  alternateDirectorData.value.startDate = newValue || '';
});

// End date 변경 시 alternateDirectorData 업데이트
watch(alternateDirectorEndDateValue, (newValue) => {
  if (props.readOnly) return;
  alternateDirectorData.value.endDate = newValue || '';
  // endDate가 있으면 hasEndDate를 true로 설정, 없으면 false로 설정
  if (newValue && newValue !== '') {
    alternateDirectorData.value.hasEndDate = true;
  } else {
    alternateDirectorData.value.hasEndDate = false;
  }
});

// End date 제거 함수
const removeEndDate = () => {
  alternateDirectorData.value.hasEndDate = false;
  alternateDirectorData.value.endDate = null as any; // null로 설정
  alternateDirectorEndDateValue.value = '';
};

// End date 추가 함수 (hasEndDate를 true로 설정)
const addEndDate = () => {
  alternateDirectorData.value.hasEndDate = true;
};

// 공통 날짜 필드 composable 사용
const {
  dateValue: appointedDateValue,
  dateDisplay: appointedDate,
  datePickerValue: appointedDatePickerValue,
  datePlaceholder,
  datePickerLocale,
  dateMenu: appointedDateMenu,
  datePickerProps // v-date-picker 공통 props (show-current, first-day-of-week 포함)
} = useDateField();

// Date of Birth 필드 composable 사용
const {
  dateValue: dateOfBirthValue,
  dateDisplay: dateOfBirthDisplay,
  datePickerValue: dateOfBirthPickerValue,
  dateMenu: dateOfBirthMenu,
} = useDateField();

// Date of Birth 변경 시 contactData 업데이트
watch(dateOfBirthValue, (newValue) => {
  if (props.readOnly) return;
  contactData.value.DATE_OF_BIRTH = newValue || '';
});

// contactData.DATE_OF_BIRTH 변경 시 dateOfBirthValue 업데이트
watch(() => contactData.value.DATE_OF_BIRTH, (newValue) => {
  if (newValue && newValue !== dateOfBirthValue.value) {
    dateOfBirthValue.value = newValue;
  }
}, { immediate: true });

// Date of Incorporation 필드 composable 사용 (회사용)
const {
  dateValue: dateOfIncorporationValue,
  dateDisplay: dateOfIncorporationDisplay,
  datePickerValue: dateOfIncorporationPickerValue,
  dateMenu: dateOfIncorporationMenu,
} = useDateField();

// Date of Incorporation 변경 시 contactData 업데이트
watch(dateOfIncorporationValue, (newValue) => {
  if (props.readOnly) return;
  contactData.value.DATE_OF_INCORPORATION = newValue || '';
});

// contactData.DATE_OF_INCORPORATION 변경 시 dateOfIncorporationValue 업데이트
watch(() => contactData.value.DATE_OF_INCORPORATION, (newValue) => {
  if (newValue && newValue !== dateOfIncorporationValue.value) {
    dateOfIncorporationValue.value = newValue;
  }
}, { immediate: true });

// Appointed Date 표시용 포맷 (10 Dec 2025 형식) - 세션의 dateFormat 사용
const appointedDateDisplay = computed(() => {
  const dateVal = appointedDateValue.value;
  return formatDocDate(dateVal);
});

// 오늘 날짜 (미래 날짜 선택 방지용)
const todayDate = computed(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});

// Appointed Date 변경 시 validation 에러 제거
watch(appointedDateValue, () => {
  if (internalValidationErrors.value.appointedDate) {
    delete internalValidationErrors.value.appointedDate;
  }
});

// Identification Type 표시용
const identificationTypeDisplay = computed(() => {
  const code = contactData.value.ID_TYPE_CD;
  if (!code) return '';
  const option = identificationTypeOptions.value.find(opt => opt.value === code);
  return option?.title || code;
});

// Nationality 표시용
const nationalityDisplay = computed(() => {
  const code = contactData.value.NATIONALITY_CD;
  if (!code) return '';
  const option = nationalityOptions.value.find(opt => opt.value === code);
  return option?.title || code;
});

// Country Code 표시용
const countryCodeDisplay = computed(() => {
  const code = contactData.value.COUNTRY_CD;
  if (!code) return '';
  const option = countryOptions.value.find(opt => opt.value === code);
  return option?.title || code;
});

// Address Type 표시용
const addressTypeDisplay = computed(() => {
  const code = contactData.value.ADDRESS_TYPE_CD;
  if (!code) return '';
  const option = addressTypeOptions.value.find(opt => opt.value === code);
  return option?.title || code;
});

// Jurisdiction 표시용 (회사용)
const jurisdictionDisplay = computed(() => {
  const code = contactData.value.JURISDICTION;
  if (!code) return '';
  const option = jurisdictionOptions.value.find(opt => opt.TOTAL_CODE === code);
  return option?.CODE_NAME || code;
});

// Representative 표시용 (회사용)
const representativeDisplay = computed(() => {
  const code = contactData.value.REPRESENTATIVE_CD;
  if (!code) return '';
  const option = representativeOptions.value.find(opt => opt.CONTACT_CD === code);
  return option?.CONTACT_NAME || code;
});

function resolveOptionValue(
  rawValue: unknown,
  options: Array<{ title?: string; value?: string; bigo1?: string; bigo3?: string }>,
): string {
  const text = rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
  if (!text) return '';
  if (options.some((option) => option.value === text)) return text;

  const lowered = text.toLowerCase();
  const matched = options.find((option) =>
    [option.title, option.bigo1, option.bigo3]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean)
      .includes(lowered),
  );

  return matched?.value || text;
}

/** REQUEST_PAYLOAD birthDate — YYYY-MM-DD (1982/03/12 등 슬래시·파싱 가능 문자열 정규화) */
function formatBirthDateForRequestPayload(raw: unknown): string {
  const s = raw === null || raw === undefined ? '' : String(raw).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const ymdSlash = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
  if (ymdSlash) {
    const y = ymdSlash[1];
    const m = String(Number(ymdSlash[2])).padStart(2, '0');
    const d = String(Number(ymdSlash[3])).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const dmySlash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmySlash) {
    const month = String(Number(dmySlash[1])).padStart(2, '0');
    const day = String(Number(dmySlash[2])).padStart(2, '0');
    return `${dmySlash[3]}-${month}-${day}`;
  }
  try {
    const date = new Date(s);
    if (!isNaN(date.getTime())) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  } catch {
    /* ignore */
  }
  return s;
}

function normalizeSelectBoundFields() {
  contactData.value.ID_TYPE_CD = resolveOptionValue(contactData.value.ID_TYPE_CD, identificationTypeOptions.value);
  contactData.value.NATIONALITY_CD = resolveOptionValue(contactData.value.NATIONALITY_CD, nationalityOptions.value);
  contactData.value.COUNTRY_CD = resolveOptionValue(contactData.value.COUNTRY_CD, countryOptions.value);
  contactData.value.ADDRESS_TYPE_CD = resolveOptionValue(contactData.value.ADDRESS_TYPE_CD, addressTypeOptions.value);
  contactData.value.ALTERNATE_ADDRESS_CD = resolveOptionValue(
    contactData.value.ALTERNATE_ADDRESS_CD,
    addressTypeOptions.value,
  );

  // FORM_DATA / L_TASK_EFILING_DATA 스냅샷: 해외 주소 줄은 있는데 타입만 B07001로 남은 경우 보정
  const pf1 = String(contactData.value.FOREIGN_ADDRESS_LINE_1 || '').trim();
  const pf2 = String(contactData.value.FOREIGN_ADDRESS_LINE_2 || '').trim();
  const pLocal = ['POSTAL_CODE', 'BLOCK_NO', 'STREET_NAME', 'BUILDING_NAME', 'ADDR_LEVEL', 'ADDR_UNIT'].some(
    (k) => String((contactData.value as Record<string, unknown>)[k] ?? '').trim(),
  );
  if ((pf1 || pf2) && !pLocal && contactData.value.ADDRESS_TYPE_CD === 'B07001') {
    contactData.value.ADDRESS_TYPE_CD = 'B07002';
  }
  const af1 = String(contactData.value.ALT_FOREIGN_ADDRESS_LINE_1 || '').trim();
  const af2 = String(contactData.value.ALT_FOREIGN_ADDRESS_LINE_2 || '').trim();
  const aLocal = ['ALT_POSTAL_CODE', 'ALT_BLOCK_NO', 'ALT_STREET_NAME', 'ALT_BUILDING_NAME', 'ALT_ADDR_LEVEL', 'ALT_ADDR_UNIT'].some(
    (k) => String((contactData.value as Record<string, unknown>)[k] ?? '').trim(),
  );
  if ((af1 || af2) && !aLocal) {
    const altCd = String(contactData.value.ALTERNATE_ADDRESS_CD || '').trim();
    if (!altCd || altCd === 'B07001') {
      contactData.value.ALTERNATE_ADDRESS_CD = 'B07002';
    }
  }
}

// Contact Address (ALT) — Residential 과 독립 (ALTERNATE_ADDRESS_CD)
const contactAddressTypeCd = computed(() => {
  const alt = String(contactData.value.ALTERNATE_ADDRESS_CD || '').trim();
  if (alt === 'B07001' || alt === 'B07002') return alt;
  return 'B07001';
});

// Contact Address가 Local인지 여부 (B07001 = Local/Residential, B07002 = Foreign/Service)
const isContactAddressLocal = computed(() => {
  return contactAddressTypeCd.value === 'B07001';
});

// BaseCode 옵션 로드 함수 (ID Type, Nationality, Country, Address Type)
const loadBaseCodeOptions = async () => {
  try {
    // ID Type (B03) - BIGO1에 E-Filing용 name 저장
    const idTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const idTypeCodes = (idTypeResponse as any).codes || idTypeResponse.data?.codes || [];
    identificationTypeOptions.value = idTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Nationality (B04) - BIGO1에 E-Filing용 name 저장
    const nationalityResponse = await baseCodeApi.getCodesByMainCode('CM', 'B04', undefined);
    const nationalityCodes = (nationalityResponse as any).codes || nationalityResponse.data?.codes || [];
    nationalityOptions.value = nationalityCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Country (B05) - bigo3 포함 (E-Filing mobileNoCountryCode용)
    const countryResponse = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
    const countryCodes = (countryResponse as any).codes || countryResponse.data?.codes || [];
    countryOptions.value = countryCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo3: code.bigo3 || code.BIGO3 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Address Type (B07) - BIGO1에 E-Filing용 name 저장
    const addressTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B07', undefined);
    const addressTypeCodes = (addressTypeResponse as any).codes || addressTypeResponse.data?.codes || [];
    addressTypeOptions.value = addressTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Company Type (A04) - E-Filing entityType용 (BIGO1에 E-Filing name 저장) - SG 국가코드 사용
    const companyTypeResponse = await baseCodeApi.getCodesByMainCode('SG', 'A04', undefined);
    const companyTypeCodes = (companyTypeResponse as any).codes || companyTypeResponse.data?.codes || [];
    companyTypeOptions.value = companyTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Jurisdiction (B15) - 회사용
    const jurisdictionResponse = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
    const jurisdictionCodes = (jurisdictionResponse as any).codes || jurisdictionResponse.data?.codes || [];
    
    // TOTAL_CODE 기준으로 중복 제거
    const uniqueJurisdictionMap = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    jurisdictionCodes.forEach((code: any) => {
      const totalCode = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      
      if (!totalCode || !codeName) return;
      
      // SUB_CODE가 '000'인 것은 제외 (마지막 3자리가 '000')
      const subCode = totalCode.slice(-3);
      if (subCode === '000') return;
      
      // 중복 제거
      if (!uniqueJurisdictionMap.has(totalCode)) {
        uniqueJurisdictionMap.set(totalCode, {
          TOTAL_CODE: totalCode,
          CODE_NAME: codeName
        });
      }
    });
    
    jurisdictionOptions.value = Array.from(uniqueJurisdictionMap.values());

    // Contact Category (B09) - E-Filing officerCategory용 (BIGO3에 E-Filing name 저장)
    const contactCategoryResponse = await baseCodeApi.getCodesByMainCode('CM', 'B09', undefined);
    const contactCategoryCodes = (contactCategoryResponse as any).codes || contactCategoryResponse.data?.codes || [];
    contactCategoryOptions.value = contactCategoryCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo3: code.bigo3 || code.BIGO3 || ''
    })).filter((opt: any) => opt.title && opt.value);

    normalizeSelectBoundFields();
  } catch (error: any) {
    console.error('❌ Failed to load BaseCode options:', error);
  }
};

// Representative Name 목록 로드 (회사용)
const loadRepresentatives = async () => {
  try {
    const response = await entityApi.getContactsList();
    const responseData = (response as any).data || response;
    const contacts = responseData.data?.contacts || responseData.contacts || [];
    
    const mappedContacts = contacts
      .filter((contact: any) => {
        // Individual만 필터링 (CATEGORY_CD = 'B09002')
        const categoryCd = contact.CATEGORY_CD || contact.categoryCd || '';
        return categoryCd === 'B09002';
      })
      .map((contact: any) => ({
        CONTACT_CD: String(contact.CONTACT_CD || contact.contactCd || '').trim(),
        CONTACT_NAME: String(contact.CONTACT_NAME || contact.contactName || '').trim(),
      }))
      .filter((item: any) => {
        return item.CONTACT_CD && item.CONTACT_NAME && item.CONTACT_CD.length > 0 && item.CONTACT_NAME.length > 0;
      });
    
    representativeOptions.value = mappedContacts;
  } catch (error) {
    console.error('❌ Failed to load representatives:', error);
    representativeOptions.value = [];
  }
};

// Director 목록 로드 (Alternate Director용)
const loadDirectors = async (entityCd: string) => {
  if (!entityCd || !entityCd.trim()) {
    directorOptions.value = [];
    return;
  }

  try {
    isLoadingDirectors.value = true;
    // Alternate Director 전용 API 사용 (REGISTER_TYPE 조건 없이 조회)
    const response = await getDirectorsByEntityAlternate(entityCd);
    const directors = (response as any).data?.officers || (response as any).officers || [];
    
    directorOptions.value = directors.map((director: any) => ({
      title: director.contactName || director.CONTACT_NAME || '',
      value: director.contactCd || director.CONTACT_CD || ''
    })).filter((opt: any) => opt.title && opt.value);
  } catch (error) {
    console.error('❌ Failed to load directors:', error);
    directorOptions.value = [];
  } finally {
    isLoadingDirectors.value = false;
  }
};

// DB 컬럼명을 사용한 필드 정의 (타입 안전성)
const entity = ref('');
const officerName = ref('');
// position은 위에서 이미 선언됨 (computed에서 사용하기 위해)

// 필드 refs (포커스 이동용)
const entityInputRef = ref<any>(null);
const positionInputRef = ref<any>(null);
const officerNameInputRef = ref<any>(null);
const appointedDateInputRef = ref<any>(null);

// Entity 이름, 등록번호, 타입 (doc_variable 및 E-Filing 매핑용)
const entityName = ref('');
const entityRegNo = ref('');
const entityCompanyType = ref('');

// 데이터 복원 중인지 여부 (watcher에서 값을 초기화하지 않도록)
const isRestoringData = ref(false);

/** setFormData 직후 watch(officerName) 한 번 — DB 조회가 FORM_DATA contactData 를 덮어쓰지 않도록 */
const skipOfficerContactDbFetchOnce = ref(false);

/** 연락처 미연결(수동 이사) 복원 시 v-autocomplete 값 — 실제 CONTACT_CD 아님 */
const MANUAL_OFFICER_SENTINEL = '__LETTAGA_MANUAL_OFFICER__';

function isManualOfficerSentinel(v: unknown): boolean {
  return String(v ?? '').trim() === MANUAL_OFFICER_SENTINEL;
}

// 내부 validation 상태 관리
const internalValidationErrors = ref<Record<string, string>>({});

// Entity 변경 시 초기화 및 Entity 상세 정보 설정 (Position은 BaseCode에서 미리 로드됨)
watch(entity, async (newEntityCd) => {
  // Entity 변경 시 validation 에러 제거
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }

  // 데이터 복원 중일 때는 초기화하지 않음
  if (!isRestoringData.value) {
    // Entity가 선택되지 않았거나 변경된 경우 초기화
    position.value = '';
    officerName.value = '';
    excludedOfficers.value = [];
    // Alternate Director 데이터도 초기화
    alternateDirectorData.value = {
      directorCd: '',
      startDate: '',
      endDate: '',
      hasEndDate: false
    };
    directorOptions.value = [];
  }
  
  // Entity 변경 시 B30024이면 Director 목록 다시 로드
  if (newEntityCd && position.value === 'B30024') {
    await loadDirectors(newEntityCd);
  }

  // officerAppointmentStore.entities에서 Entity 상세 정보 가져오기
  if (newEntityCd) {
    // entities가 아직 로드되지 않은 경우 대기 (최대 3초)
    let retryCount = 0;
    const maxRetries = 30;
    while (officerAppointmentStore.entities.length === 0 && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retryCount++;
    }

    const entityData = officerAppointmentStore.entities.find(
      (e: any) => (e.entityCd || e.ENTITY_CD) === newEntityCd
    );
    if (entityData) {
      entityName.value = (entityData as any).companyName || (entityData as any).COMPANY_NAME || '';
      entityRegNo.value = (entityData as any).regNo || (entityData as any).REG_NO || '';
      entityCompanyType.value = (entityData as any).companyType || (entityData as any).COMPANY_TYPE || '';
      console.log('📋 Entity details from store:', {
        entityName: entityName.value,
        entityRegNo: entityRegNo.value,
        entityCompanyType: entityCompanyType.value,
        retryCount
      });
    } else {
      console.warn('⚠️ Entity not found in store:', newEntityCd);
      entityName.value = '';
      entityRegNo.value = '';
      entityCompanyType.value = '';
    }
  } else {
    entityName.value = '';
    entityRegNo.value = '';
    entityCompanyType.value = '';
  }
});

// Position 변경 시 제외할 Officer 목록 조회 및 선택된 Officer 초기화
// 그리고 기존 taskConfig.documents에서 filter로 필터링하여 emit (별도 API 호출 없음)
watch(position, async (newPositionCd) => {
  // Position 변경 시 Task Flow 상태 업데이트 (E-Filing 활성화/비활성화)
  updateTaskFlowState();

  // Position 변경 시 validation 에러 제거
  if (internalValidationErrors.value.position) {
    delete internalValidationErrors.value.position;
  }

  // 데이터 복원 중일 때는 Officer 초기화하지 않음
  if (!isRestoringData.value) {
    // Position 변경 시 선택된 Officer 초기화
    officerName.value = '';
  }

  if (!newPositionCd || !newPositionCd.trim() || !entity.value || !entity.value.trim()) {
    excludedOfficers.value = [];
    // Position이 선택 해제되면 템플릿 초기화
    emit('reset-templates');
    return;
  }

  // Position이 선택되면 해당 Position의 Officer 목록 조회 (제외할 대상)
  await loadOfficers(entity.value, newPositionCd);

  // B30024 (Alternate Director) 선택 시 Director 목록 로드
  if (newPositionCd === 'B30024' && entity.value) {
    await loadDirectors(entity.value);
  } else {
    // 다른 Position 선택 시 Director 목록 초기화
    directorOptions.value = [];
    alternateDirectorData.value = {
      directorCd: '',
      startDate: '',
      endDate: '',
      hasEndDate: false
    };
  }

  // Position 변경 시 taskConfig.documents에서 filter로 필터링하여 emit
  const taskConfig = props.taskConfig;
  if (taskConfig && taskConfig.documents && Array.isArray(taskConfig.documents)) {
    // 해당 Position(filter)에 맞는 문서만 필터링
    const filteredDocuments = taskConfig.documents.filter(
      doc => doc.filter === newPositionCd
    );

    if (filteredDocuments.length > 0) {
      const templateIds = filteredDocuments.map(doc => doc.DOC_TYPE).filter(id => id);
      const categoryName = filteredDocuments[0]?.category?.trim() || null;

      console.log('📋 [OfficerAppointmentForm] Filter로 문서 필터링:', {
        filterValue: newPositionCd,
        totalDocuments: taskConfig.documents.length,
        filteredCount: filteredDocuments.length,
        templateIds,
        categoryName
      });

      // filter로 필터링한 문서 목록 emit
      emit('filter-select-templates', {
        categoryName: categoryName || '',
        templateIds,
        filterValue: newPositionCd
      });
    } else {
      // 해당 filter에 맞는 문서가 없으면 초기화
      console.log('📋 [OfficerAppointmentForm] 해당 Filter의 문서 없음 - 초기화:', {
        filterValue: newPositionCd
      });
      emit('reset-templates');
    }
  }
});

// Officer Name 선택 시 Contact 정보 자동 바인딩
watch(officerName, async (contactCd) => {
  // 데이터 복원 중이면 DB 조회하지 않음 (setFormData에서 이미 설정됨)
  if (isRestoringData.value) {
    console.log('📋 [OfficerAppointmentForm] 데이터 복원 중 - Contact DB 조회 건너뜀');
    return;
  }

  // Officer Name 변경 시 validation 에러 제거
  if (internalValidationErrors.value.officerName) {
    delete internalValidationErrors.value.officerName;
  }

  if (isManualOfficerSentinel(contactCd)) {
    return;
  }

  if (skipOfficerContactDbFetchOnce.value) {
    skipOfficerContactDbFetchOnce.value = false;
    return;
  }

  // AUDITOR(B30025) 선택 시 확인 알림 표시
  if (contactCd && position.value === 'B30025') {
    const confirmMessage = t('startTask.Only Public Accounting Entity can be designated as Auditor') || 'Only Public Accounting Entity can be designated as Auditor';
    const confirmed = await confirm({
      title: t('startTask.Confirm') || 'Confirm',
      message: confirmMessage,
      type: 'question',
      confirmText: t('startTask.Confirm') || 'Confirm',
      cancelText: t('startTask.Cancel') || 'Cancel'
    });
    
    if (!confirmed) {
      // 사용자가 취소하면 officerName을 초기화
      officerName.value = '';
      return;
    }
  }

  if (!contactCd) {
    // 선택 해제 시 초기화
    contactData.value = {
      // Basic 섹션
      CONTACT_NAME: '',
      FIRST_NAME: '',
      MIDDLE_NAME: '',
      SURNAME: '',
      ID_TYPE_CD: '',
      CONTACT_REG_NO: '',
      NATIONALITY_CD: '',
      DATE_OF_BIRTH: '',
        CATEGORY_CD: '', // E-Filing officerCategory용 (B09001: Corporate, B09002: Individual)
        // Corporate 필드 (CATEGORY_CD = 'B09001')
        JURISDICTION: '',
        AUDITOR_YN: 'No', // 'Yes' or 'No'
        DATE_OF_INCORPORATION: '',
        REPRESENTATIVE_CD: '',
        // Residential Address 섹션
        ADDRESS_TYPE_CD: 'B07001',
      POSTAL_CODE: '',
      BLOCK_NO: '',
      STREET_NAME: '',
      BUILDING_NAME: '',
      ADDR_LEVEL: '',
      ADDR_UNIT: '',
      FOREIGN_ADDRESS_LINE_1: '',
      FOREIGN_ADDRESS_LINE_2: '',
      // Contact Address 섹션 (대체 주소)
      ALTERNATE_ADDRESS_CD: '',
      ALT_POSTAL_CODE: '',
      ALT_BLOCK_NO: '',
      ALT_STREET_NAME: '',
      ALT_BUILDING_NAME: '',
      ALT_ADDR_LEVEL: '',
      ALT_ADDR_UNIT: '',
      ALT_FOREIGN_ADDRESS_LINE_1: '',
      ALT_FOREIGN_ADDRESS_LINE_2: '',
      // Contact 섹션
      COUNTRY_CD: '',
      AREA_CODE: '',
      MOBILE_NO: '',
      CONTACT_EMAIL: ''
    };
    return;
  }

  try {
    // Contact 상세 정보 조회
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;

    if (contact) {
      // AUDITOR_YN 처리 (boolean 또는 'Yes'/'No' 문자열)
      const auditorYnValue = contact.AUDITOR_YN !== undefined 
        ? (contact.AUDITOR_YN === true || contact.AUDITOR_YN === 1 || contact.AUDITOR_YN === 'Yes' ? 'Yes' : 'No')
        : (contact.AUDITTING !== undefined
          ? (contact.AUDITTING === 1 || contact.AUDITTING === true ? 'Yes' : 'No')
          : 'No');
      
      // Contact 데이터 바인딩
      contactData.value = {
        // Basic 섹션
        CONTACT_NAME: contact.CONTACT_NAME || contact.contactName || '',
        FIRST_NAME: contact.FIRST_NAME || contact.firstName || '',
        MIDDLE_NAME: contact.MIDDLE_NAME || contact.middleName || '',
        SURNAME: contact.SURNAME || contact.surname || '',
        ID_TYPE_CD: contact.ID_TYPE_CD || contact.idTypeCd || '',
        CONTACT_REG_NO: contact.CONTACT_REG_NO || contact.contactRegNo || '',
        NATIONALITY_CD: contact.NATIONALITY_CD || contact.nationalityCd || '',
        DATE_OF_BIRTH: (contact.DATE_OF_BIRTH || contact.dateOfBirth || contact.date_of_birth || contact.DOB || '').substring(0, 10),
        CATEGORY_CD: contact.CATEGORY_CD || contact.categoryCd || '', // E-Filing officerCategory용 (B09001: Corporate, B09002: Individual)
        // Corporate 필드 (CATEGORY_CD = 'B09001')
        JURISDICTION: contact.JURISDICTION || contact.jurisdiction || '',
        AUDITOR_YN: (() => {
          // AUDITOR_YN 처리 (boolean 또는 'Yes'/'No' 문자열)
          const auditorYnValue = contact.AUDITOR_YN !== undefined 
            ? (contact.AUDITOR_YN === true || contact.AUDITOR_YN === 1 || contact.AUDITOR_YN === 'Yes' ? 'Yes' : 'No')
            : (contact.AUDITTING !== undefined
              ? (contact.AUDITTING === 1 || contact.AUDITTING === true ? 'Yes' : 'No')
              : 'No');
          return auditorYnValue;
        })(),
        DATE_OF_INCORPORATION: (contact.DATE_OF_INCORPORATION || contact.dateOfIncorporation || contact.incorporationDate || '').substring(0, 10),
        REPRESENTATIVE_CD: contact.REPRESENTATIVE_CD || contact.representativeCd || contact.representativeName || '',
        // Residential Address 섹션
        ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || contact.addressTypeCd || 'B07001',
        POSTAL_CODE: contact.POSTAL_CODE || contact.postalCode || '',
        BLOCK_NO: contact.BLOCK_NO || contact.blockNo || '',
        STREET_NAME: contact.STREET_NAME || contact.streetName || '',
        BUILDING_NAME: contact.BUILDING_NAME || contact.buildingName || '',
        ADDR_LEVEL: contact.ADDR_LEVEL || contact.addrLevel || '',
        ADDR_UNIT: contact.ADDR_UNIT || contact.addrUnit || '',
        FOREIGN_ADDRESS_LINE_1: contact.FOREIGN_ADDRESS_LINE_1 || contact.foreignAddressLine1 || '',
        FOREIGN_ADDRESS_LINE_2: contact.FOREIGN_ADDRESS_LINE_2 || contact.foreignAddressLine2 || '',
        // Contact Address 섹션 (대체 주소)
        ALTERNATE_ADDRESS_CD: contact.ALTERNATE_ADDRESS_CD || contact.alternateAddressCd || '',
        ALT_POSTAL_CODE: contact.ALT_POSTAL_CODE || contact.altPostalCode || '',
        ALT_BLOCK_NO: contact.ALT_BLOCK_NO || contact.altBlockNo || '',
        ALT_STREET_NAME: contact.ALT_STREET_NAME || contact.altStreetName || '',
        ALT_BUILDING_NAME: contact.ALT_BUILDING_NAME || contact.altBuildingName || '',
        ALT_ADDR_LEVEL: contact.ALT_ADDR_LEVEL || contact.altAddrLevel || '',
        ALT_ADDR_UNIT: contact.ALT_ADDR_UNIT || contact.altAddrUnit || '',
        ALT_FOREIGN_ADDRESS_LINE_1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || contact.altForeignAddressLine1 || '',
        ALT_FOREIGN_ADDRESS_LINE_2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || contact.altForeignAddressLine2 || '',
        // Contact 섹션
        COUNTRY_CD: contact.COUNTRY_CD || contact.countryCd || '',
        AREA_CODE: contact.AREA_CODE || contact.areaCode || '',
        MOBILE_NO: contact.MOBILE_NO || contact.mobileNo || '',
        CONTACT_EMAIL: contact.CONTACT_EMAIL || contact.contactEmail || ''
      };
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch contact details:', error);
  }
});

// 이미 자동 선택 이벤트를 발생시켰는지 추적
let hasEmittedAutoSelect = false;

// 카테고리/파일 자동 선택 함수 (Position과 별개로 동작)
// filter 값이 없는 문서만 자동 선택 (filter가 있는 문서는 Position 변경 시 별도 처리)
const autoSelectCategoryAndFiles = () => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    return;
  }

  // 이미 같은 taskConfig로 이벤트를 발생시켰으면 스킵
  if (hasEmittedAutoSelect) {
    return;
  }

  try {
    // 문서(템플릿) 자동 선택 - filter가 없는 문서만
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // filter가 없는(null) 문서만 필터링
      const documentsWithoutFilter = taskConfig.documents.filter(
        doc => !doc.filter // filter가 null, undefined, '' 인 경우
      );

      if (documentsWithoutFilter.length > 0) {
        const templateIds = documentsWithoutFilter
          .map(doc => doc.DOC_TYPE)
          .filter(id => id); // null/undefined 제거

        // 카테고리명 = documents의 category (DIRECTORY에서 온 값)
        const categoryName = documentsWithoutFilter[0]?.category?.trim() || null;

        console.log('📋 [OfficerAppointmentForm] Filter 없는 문서 자동 선택:', {
          totalDocuments: taskConfig.documents.length,
          documentsWithoutFilter: documentsWithoutFilter.length,
          templateIds,
          categoryName
        });

        // 템플릿 자동 선택 이벤트 emit
        emit('auto-select-templates', {
          categoryName: categoryName || '',
          templateIds
        });

        // 이벤트 발생 완료 표시
        hasEmittedAutoSelect = true;
      } else {
        console.log('📋 [OfficerAppointmentForm] Filter 없는 문서가 없음 - Position 선택 시 문서 선택됨');
        hasEmittedAutoSelect = true; // 이벤트는 발생시키지 않지만 플래그는 설정
      }
    }
  } catch (error) {
    console.error('Failed to auto-select templates:', error);
  }
};

// taskConfig가 변경될 때 카테고리/파일 자동 선택 (Position과 별개)
watch(() => props.taskConfig, (newConfig, oldConfig) => {
  if (newConfig) {
    // taskConfig가 실제로 변경되었을 때만 플래그 리셋
    if (newConfig !== oldConfig) {
      hasEmittedAutoSelect = false;
    }
    autoSelectCategoryAndFiles();
  }
}, { immediate: true });

// 초기 데이터 로드
onMounted(async () => {
  try {
    await Promise.all([
      officerAppointmentStore.fetchEntities(),
      loadAllOfficers(), // 전체 Officer 목록 조회
      loadPositionCodes(), // Position 코드 로드 (BaseCode에서)
      loadBaseCodeOptions(), // BaseCode 옵션 로드 (ID Type, Nationality, Country, Address Type, Jurisdiction)
      loadRepresentatives() // Representative 목록 로드 (회사용)
    ]);
  } catch (error) {
    console.error('Failed to fetch initial data:', error);
  }
});

const handleNewContact = () => {
  if (officerName.value && !isManualOfficerSentinel(officerName.value)) {
    showContactDetailModal.value = true;
  } else {
    // Officer Name이 선택되지 않았으면 Contact 리스트로 이동
    contactListInitialSearch.value = '';
    showContactListModal.value = true;
  }
};

// Contact 상세 화면에서 선택 시 처리 (Select 버튼 클릭 시)
const handleContactDetailSelect = (contact: any) => {
  if (contact && (contact.CONTACT_CD || contact.contactCd)) {
    const contactCd = contact.CONTACT_CD || contact.contactCd;
    officerName.value = contactCd;
    showContactDetailModal.value = false;
  }
};

// Contact 업데이트 진행 중 플래그
const isContactUpdating = ref(false);

// Contact 상세 화면에서 업데이트 시 처리
const handleContactUpdated = async (contact: any) => {
  console.log('📋 [OfficerAppointmentForm] handleContactUpdated 호출됨', { contact, officerName: officerName.value });
  isContactUpdating.value = true;

  try {
    // Contact 정보가 업데이트되면 officerOptions를 다시 로드하여 최신 정보 반영
    await loadAllOfficers();
    if (entity.value && position.value) {
      await loadOfficers(entity.value, position.value);
    }
    if (officerName.value && !isManualOfficerSentinel(officerName.value)) {
      console.log('📋 [OfficerAppointmentForm] Contact 데이터 다시 조회 중...', officerName.value);
      const response = await contactsApi.getContactById(officerName.value);
      const updatedContact = (response as any).contact || response.data?.contact;
      console.log('📋 [OfficerAppointmentForm] 조회된 Contact 데이터:', updatedContact);
      console.log('📋 [OfficerAppointmentForm] DATE_OF_BIRTH 필드 확인:', {
        DATE_OF_BIRTH: updatedContact?.DATE_OF_BIRTH,
        dateOfBirth: updatedContact?.dateOfBirth,
        date_of_birth: updatedContact?.date_of_birth
      });
      if (updatedContact) {
        // AUDITOR_YN 처리 (boolean 또는 'Yes'/'No' 문자열)
        const auditorYnValue = updatedContact.AUDITOR_YN !== undefined 
          ? (updatedContact.AUDITOR_YN === true || updatedContact.AUDITOR_YN === 1 || updatedContact.AUDITOR_YN === 'Yes' ? 'Yes' : 'No')
          : (updatedContact.AUDITTING !== undefined
            ? (updatedContact.AUDITTING === 1 || updatedContact.AUDITTING === true ? 'Yes' : 'No')
            : 'No');
        
        contactData.value = {
          // Basic 섹션
          CONTACT_NAME: updatedContact.CONTACT_NAME || updatedContact.contactName || '',
          FIRST_NAME: updatedContact.FIRST_NAME || updatedContact.firstName || '',
          MIDDLE_NAME: updatedContact.MIDDLE_NAME || updatedContact.middleName || '',
          SURNAME: updatedContact.SURNAME || updatedContact.surname || '',
          ID_TYPE_CD: updatedContact.ID_TYPE_CD || updatedContact.idTypeCd || '',
          CONTACT_REG_NO: updatedContact.CONTACT_REG_NO || updatedContact.contactRegNo || '',
          NATIONALITY_CD: updatedContact.NATIONALITY_CD || updatedContact.nationalityCd || '',
          DATE_OF_BIRTH: (updatedContact.DATE_OF_BIRTH || updatedContact.dateOfBirth || updatedContact.date_of_birth || updatedContact.DOB || '').substring(0, 10),
          CATEGORY_CD: updatedContact.CATEGORY_CD || updatedContact.categoryCd || '', // E-Filing officerCategory용 (B09001: Corporate, B09002: Individual)
          // Corporate 필드 (CATEGORY_CD = 'B09001')
          JURISDICTION: updatedContact.JURISDICTION || updatedContact.jurisdiction || '',
          AUDITOR_YN: (() => {
            // AUDITOR_YN 처리 (boolean 또는 'Yes'/'No' 문자열)
            const auditorYnValue = updatedContact.AUDITOR_YN !== undefined 
              ? (updatedContact.AUDITOR_YN === true || updatedContact.AUDITOR_YN === 1 || updatedContact.AUDITOR_YN === 'Yes' ? 'Yes' : 'No')
              : (updatedContact.AUDITTING !== undefined
                ? (updatedContact.AUDITTING === 1 || updatedContact.AUDITTING === true ? 'Yes' : 'No')
                : 'No');
            return auditorYnValue;
          })(),
          DATE_OF_INCORPORATION: (updatedContact.DATE_OF_INCORPORATION || updatedContact.dateOfIncorporation || updatedContact.incorporationDate || '').substring(0, 10),
          REPRESENTATIVE_CD: updatedContact.REPRESENTATIVE_CD || updatedContact.representativeCd || updatedContact.representativeName || '',
          // Residential Address 섹션
          ADDRESS_TYPE_CD: updatedContact.ADDRESS_TYPE_CD || updatedContact.addressTypeCd || 'B07001',
          POSTAL_CODE: updatedContact.POSTAL_CODE || updatedContact.postalCode || '',
          BLOCK_NO: updatedContact.BLOCK_NO || updatedContact.blockNo || '',
          STREET_NAME: updatedContact.STREET_NAME || updatedContact.streetName || '',
          BUILDING_NAME: updatedContact.BUILDING_NAME || updatedContact.buildingName || '',
          ADDR_LEVEL: updatedContact.ADDR_LEVEL || updatedContact.addrLevel || '',
          ADDR_UNIT: updatedContact.ADDR_UNIT || updatedContact.addrUnit || '',
          FOREIGN_ADDRESS_LINE_1: updatedContact.FOREIGN_ADDRESS_LINE_1 || updatedContact.foreignAddressLine1 || '',
          FOREIGN_ADDRESS_LINE_2: updatedContact.FOREIGN_ADDRESS_LINE_2 || updatedContact.foreignAddressLine2 || '',
          // Contact Address 섹션 (대체 주소)
          ALTERNATE_ADDRESS_CD: updatedContact.ALTERNATE_ADDRESS_CD || updatedContact.alternateAddressCd || '',
          ALT_POSTAL_CODE: updatedContact.ALT_POSTAL_CODE || updatedContact.altPostalCode || '',
          ALT_BLOCK_NO: updatedContact.ALT_BLOCK_NO || updatedContact.altBlockNo || '',
          ALT_STREET_NAME: updatedContact.ALT_STREET_NAME || updatedContact.altStreetName || '',
          ALT_BUILDING_NAME: updatedContact.ALT_BUILDING_NAME || updatedContact.altBuildingName || '',
          ALT_ADDR_LEVEL: updatedContact.ALT_ADDR_LEVEL || updatedContact.altAddrLevel || '',
          ALT_ADDR_UNIT: updatedContact.ALT_ADDR_UNIT || updatedContact.altAddrUnit || '',
          ALT_FOREIGN_ADDRESS_LINE_1: updatedContact.ALT_FOREIGN_ADDRESS_LINE_1 || updatedContact.altForeignAddressLine1 || '',
          ALT_FOREIGN_ADDRESS_LINE_2: updatedContact.ALT_FOREIGN_ADDRESS_LINE_2 || updatedContact.altForeignAddressLine2 || '',
          // Contact 섹션
          COUNTRY_CD: updatedContact.COUNTRY_CD || updatedContact.countryCd || '',
          AREA_CODE: updatedContact.AREA_CODE || updatedContact.areaCode || '',
          MOBILE_NO: updatedContact.MOBILE_NO || updatedContact.mobileNo || '',
          CONTACT_EMAIL: updatedContact.CONTACT_EMAIL || updatedContact.contactEmail || ''
        };
        
        // Date of Incorporation 설정
        if (contactData.value.DATE_OF_INCORPORATION) {
          dateOfIncorporationValue.value = contactData.value.DATE_OF_INCORPORATION;
        }
        
        console.log('📋 [OfficerAppointmentForm] contactData 업데이트 완료:', contactData.value);
      }
    }
  } catch (error) {
    console.error('Failed to refresh contact data:', error);
  } finally {
    isContactUpdating.value = false;
    // 업데이트 완료 후 모달 닫기
    showContactDetailModal.value = false;
    console.log('📋 [OfficerAppointmentForm] handleContactUpdated 완료, 모달 닫힘');
  }
};

// Contact 선택 핸들러
const handleContactSelect = (contact: any) => {
  // 선택한 Contact의 CONTACT_CD 가져오기
  const contactCd = contact.CONTACT_CD || '';
  
  // officerName에 설정 (CONTACT_CD 사용)
  officerName.value = contactCd;
  
  // 모달 닫기
  showContactListModal.value = false;
};

// 필수 검증 규칙 (validationErrors와 internalValidationErrors 모두 확인)
const officerNameRules = computed(() => {
  const rules: Array<(v: any) => boolean | string> = [];
  // props.validationErrors가 있고 해당 필드에 에러가 있으면 표시
  if (props.validationErrors && Object.keys(props.validationErrors).length > 0 && props.validationErrors?.officerName) {
    rules.push(() => props.validationErrors.officerName || '');
  }
  // internalValidationErrors가 있으면 표시
  if (internalValidationErrors.value.officerName) {
    rules.push(() => internalValidationErrors.value.officerName || '');
  }
  rules.push((v: string) => !!v || t('startTask.This field is required') || 'This field is required');
  return rules;
});

const appointedDateRules = computed(() => {
  const rules: Array<(v: any) => boolean | string> = [];
  if (props.validationErrors && Object.keys(props.validationErrors).length > 0 && props.validationErrors?.appointedDate) {
    rules.push(() => props.validationErrors.appointedDate || '');
  }
  // internalValidationErrors가 있으면 표시
  if (internalValidationErrors.value.appointedDate) {
    rules.push(() => internalValidationErrors.value.appointedDate || '');
  }
  rules.push((v: string) => !!v || t('startTask.This field is required') || 'This field is required');
  return rules;
});

// Entity validation error messages (computed)
const entityErrorMessages = computed(() => {
  if (props.validationErrors?.entity) {
    return [props.validationErrors.entity];
  }
  if (internalValidationErrors.value.entity) {
    return [internalValidationErrors.value.entity];
  }
  return [];
});

// Position validation error messages (computed)
const positionErrorMessages = computed(() => {
  const errors: string[] = [];
  if (props.validationErrors?.position) {
    errors.push(props.validationErrors.position);
  }
  if (internalValidationErrors.value.position) {
    errors.push(internalValidationErrors.value.position);
  }
  return errors;
});

// Position validation rules는 제거됨 - :error-messages로 직접 사용

// Position 코드에서 표시 텍스트 가져오기
const getPositionText = (positionCode: string): string => {
  const positionItem = positionOptions.value.find(opt => opt.value === positionCode);
  return positionItem?.title || positionCode || '';
};

// Officer 코드(contactCd)에서 실제 이름(contactName) 가져오기
const getOfficerDisplayName = (officerCode: string): string => {
  if (isManualOfficerSentinel(officerCode)) {
    return String(contactData.value.CONTACT_NAME || '').trim();
  }
  const officerItem = officerOptions.value.find(opt => opt.value === officerCode);
  return officerItem?.title || officerCode || '';
};

// Entity 검증 함수
const validateEntity = (): boolean => {
  if (!entity.value) {
    internalValidationErrors.value.entity = t('startTask.This field is required') || 'This field is required';
    // Entity 필드에 포커스 주기 위해 nextTick 사용
    nextTick(() => {
      if (entityInputRef.value) {
        try {
          // Vuetify 컴포넌트의 focus 메서드 호출
          if (typeof entityInputRef.value.focus === 'function') {
            entityInputRef.value.focus();
          }
          // 스크롤 이동
          const element = entityInputRef.value.$el || entityInputRef.value.$refs?.input?.$el || document.querySelector('[data-field="entityName"]')?.closest('.v-autocomplete');
          if (element) {
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (error) {
          console.warn('Failed to focus entity input:', error);
        }
      } else {
        // ref가 없을 경우 fallback으로 DOM 쿼리 사용
        const entityInput = document.querySelector('[data-field="entityName"]')?.closest('.v-autocomplete');
        if (entityInput) {
          (entityInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = (entityInput as HTMLElement).querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }
    });
    return false;
  }
  // 검증 통과 시 에러 제거
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }
  return true;
};

// Officer Name 검증 함수 (Task 생성 시 필수)
const validateOfficerName = (): boolean => {
  if (!officerName.value) {
    internalValidationErrors.value.officerName = t('startTask.This field is required') || 'This field is required';
    // Officer Name 필드에 포커스 주기 위해 nextTick 사용
    nextTick(() => {
      if (officerNameInputRef.value) {
        try {
          // Vuetify 컴포넌트의 focus 메서드 호출
          if (typeof officerNameInputRef.value.focus === 'function') {
            officerNameInputRef.value.focus();
          }
          // 스크롤 이동
          const element = officerNameInputRef.value.$el || officerNameInputRef.value.$refs?.input?.$el || document.querySelector('[data-field="officerName"]')?.closest('.v-autocomplete');
          if (element) {
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (error) {
          console.warn('Failed to focus officer name input:', error);
        }
      } else {
        // ref가 없을 경우 fallback으로 DOM 쿼리 사용
        const officerInput = document.querySelector('[data-field="officerName"]')?.closest('.v-autocomplete');
        if (officerInput) {
          (officerInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = (officerInput as HTMLElement).querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }
    });
    return false;
  }
  // 검증 통과 시 에러 제거
  if (internalValidationErrors.value.officerName) {
    delete internalValidationErrors.value.officerName;
  }
  return true;
};

// Position 검증 함수
const validatePosition = (): boolean => {
  if (!position.value) {
    internalValidationErrors.value.position = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      if (positionInputRef.value) {
        try {
          // Vuetify 컴포넌트의 focus 메서드 호출
          if (typeof positionInputRef.value.focus === 'function') {
            positionInputRef.value.focus();
          }
          // 스크롤 이동
          const element = positionInputRef.value.$el || positionInputRef.value.$refs?.input?.$el || document.querySelector('.v-select[aria-label*="Position"]')?.closest('.v-select');
          if (element) {
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (error) {
          console.warn('Failed to focus position input:', error);
        }
      } else {
        // ref가 없을 경우 fallback으로 DOM 쿼리 사용
        const positionInput = document.querySelector('.v-select[aria-label*="Position"]')?.closest('.v-select');
        if (positionInput) {
          (positionInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = (positionInput as HTMLElement).querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }
    });
    return false;
  }
  if (internalValidationErrors.value.position) {
    delete internalValidationErrors.value.position;
  }
  return true;
};

// Appointed Date 검증 함수
const validateAppointedDate = (): boolean => {
  if (!appointedDateValue.value) {
    internalValidationErrors.value.appointedDate = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      if (appointedDateInputRef.value) {
        try {
          // Vuetify 컴포넌트의 focus 메서드 호출
          if (typeof appointedDateInputRef.value.focus === 'function') {
            appointedDateInputRef.value.focus();
          }
          // 스크롤 이동
          const element = appointedDateInputRef.value.$el || appointedDateInputRef.value.$refs?.input?.$el || document.querySelector('[data-field="appointedDate"]')?.closest('.v-text-field');
          if (element) {
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (error) {
          console.warn('Failed to focus appointed date input:', error);
        }
      } else {
        // ref가 없을 경우 fallback으로 DOM 쿼리 사용
        const dateInput = document.querySelector('[data-field="appointedDate"]')?.closest('.v-text-field');
        if (dateInput) {
          (dateInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = (dateInput as HTMLElement).querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }
    });
    return false;
  }
  if (internalValidationErrors.value.appointedDate) {
    delete internalValidationErrors.value.appointedDate;
  }
  return true;
};

// Corporate 필드 검증 함수들 (E-Filing 모드일 때만)
const validateJurisdiction = (): boolean => {
  if (!eFilingMode || !isCorporate.value) {
    return true; // E-Filing 모드가 아니거나 회사가 아니면 검증하지 않음
  }
  
  if (!contactData.value.JURISDICTION || contactData.value.JURISDICTION.trim() === '') {
    internalValidationErrors.value.jurisdiction = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      const field = document.querySelector('[data-field="jurisdiction"]') as HTMLElement;
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('input') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  
  delete internalValidationErrors.value.jurisdiction;
  return true;
};

const validateEntityName = (): boolean => {
  if (!eFilingMode || !isCorporate.value) {
    return true; // E-Filing 모드가 아니거나 회사가 아니면 검증하지 않음
  }
  
  if (!contactData.value.CONTACT_NAME || contactData.value.CONTACT_NAME.trim() === '') {
    internalValidationErrors.value.entityName = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      const field = document.querySelector('[data-field="entityName"]') as HTMLElement;
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('input') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  
  delete internalValidationErrors.value.entityName;
  return true;
};

const validateRegistrationNumber = (): boolean => {
  if (!eFilingMode || !isCorporate.value) {
    return true; // E-Filing 모드가 아니거나 회사가 아니면 검증하지 않음
  }
  
  if (!contactData.value.CONTACT_REG_NO || contactData.value.CONTACT_REG_NO.trim() === '') {
    internalValidationErrors.value.registrationIdNo = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      const field = document.querySelector('[data-field="registrationNumber"]') as HTMLElement;
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('input') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  
  delete internalValidationErrors.value.registrationIdNo;
  return true;
};

// 개인 필드 검증 함수들 (E-Filing 모드일 때만)
const validateResidentialPostalCode = (): boolean => {
  if (!eFilingMode) {
    return true; // E-Filing 모드가 아니면 검증하지 않음
  }
  
  const addressTypeCd = contactData.value.ADDRESS_TYPE_CD || 'B07001';
  if (addressTypeCd === 'B07001') {
    // Local Address인 경우 Postal Code 필수
    if (!contactData.value.POSTAL_CODE || contactData.value.POSTAL_CODE.trim() === '') {
      internalValidationErrors.value.postalCode = t('startTask.This field is required') || 'This field is required';
      nextTick(() => {
        const field = document.querySelector('[data-field="residentialPostalCode"]') as HTMLElement;
        if (field) {
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = field.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
          }
        }
      });
      return false;
    }
    delete internalValidationErrors.value.postalCode;
  }
  return true;
};

const validateResidentialForeignAddr1 = (): boolean => {
  if (!eFilingMode) {
    return true; // E-Filing 모드가 아니면 검증하지 않음
  }
  
  const addressTypeCd = contactData.value.ADDRESS_TYPE_CD || 'B07001';
  if (addressTypeCd === 'B07002') {
    // Foreign Address인 경우 Foreign Address Line 1 필수
    if (!contactData.value.FOREIGN_ADDRESS_LINE_1 || contactData.value.FOREIGN_ADDRESS_LINE_1.trim() === '') {
      internalValidationErrors.value.foreignAddressLine1 = t('startTask.This field is required') || 'This field is required';
      nextTick(() => {
        const field = document.querySelector('[data-field="residentialForeignAddr1"]') as HTMLElement;
        if (field) {
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = field.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
          }
        }
      });
      return false;
    }
    delete internalValidationErrors.value.foreignAddressLine1;
  }
  return true;
};

const validateContactPostalCode = (): boolean => {
  if (!eFilingMode || !isIndividual.value) {
    return true; // E-Filing 모드가 아니거나 개인이 아니면 검증하지 않음
  }
  
  const alternateAddressCd = contactData.value.ALTERNATE_ADDRESS_CD || contactData.value.ADDRESS_TYPE_CD || 'B07001';
  if (alternateAddressCd === 'B07001') {
    // Local Address인 경우 Postal Code 필수
    if (!contactData.value.ALT_POSTAL_CODE || contactData.value.ALT_POSTAL_CODE.trim() === '') {
      internalValidationErrors.value.contactPostalCode = t('startTask.This field is required') || 'This field is required';
      nextTick(() => {
        const field = document.querySelector('[data-field="contactPostalCode"]') as HTMLElement;
        if (field) {
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = field.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
          }
        }
      });
      return false;
    }
    delete internalValidationErrors.value.contactPostalCode;
  }
  return true;
};

const validateContactForeignAddr1 = (): boolean => {
  if (!eFilingMode || !isIndividual.value) {
    return true; // E-Filing 모드가 아니거나 개인이 아니면 검증하지 않음
  }
  
  const alternateAddressCd = contactData.value.ALTERNATE_ADDRESS_CD || contactData.value.ADDRESS_TYPE_CD || 'B07001';
  if (alternateAddressCd === 'B07002') {
    // Foreign Address인 경우 Foreign Address Line 1 필수
    if (!contactData.value.ALT_FOREIGN_ADDRESS_LINE_1 || contactData.value.ALT_FOREIGN_ADDRESS_LINE_1.trim() === '') {
      internalValidationErrors.value.contactForeignAddr1 = t('startTask.This field is required') || 'This field is required';
      nextTick(() => {
        const field = document.querySelector('[data-field="contactForeignAddr1"]') as HTMLElement;
        if (field) {
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = field.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
          }
        }
      });
      return false;
    }
    delete internalValidationErrors.value.contactForeignAddr1;
  }
  return true;
};

const validateContactEmail = (): boolean => {
  if (!eFilingMode || !isIndividual.value) {
    return true; // E-Filing 모드가 아니거나 개인이 아니면 검증하지 않음
  }
  
  if (!contactData.value.CONTACT_EMAIL || contactData.value.CONTACT_EMAIL.trim() === '') {
    internalValidationErrors.value.email = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      const field = document.querySelector('[data-field="contactEmail"]') as HTMLElement;
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('input') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  
  delete internalValidationErrors.value.email;
  return true;
};

const validateContactMobileNoCountryCode = (): boolean => {
  if (!eFilingMode || !isIndividual.value) {
    return true; // E-Filing 모드가 아니거나 개인이 아니면 검증하지 않음
  }
  
  const countryCd = contactData.value.COUNTRY_CD || '';
  if (!countryCd || countryCd.trim() === '') {
    internalValidationErrors.value.mobileNoCountryCode = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      const field = document.querySelector('[data-field="contactMobileNoCountryCode"]') as HTMLElement;
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('input') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  
  delete internalValidationErrors.value.mobileNoCountryCode;
  return true;
};

const validateContactMobileNo = (): boolean => {
  if (!eFilingMode || !isIndividual.value) {
    return true; // E-Filing 모드가 아니거나 개인이 아니면 검증하지 않음
  }
  
  if (!contactData.value.MOBILE_NO || contactData.value.MOBILE_NO.trim() === '') {
    internalValidationErrors.value.mobileNo = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      const field = document.querySelector('[data-field="contactMobileNo"]') as HTMLElement;
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('input') as HTMLElement;
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  
  delete internalValidationErrors.value.mobileNo;
  return true;
};

// 전체 필수 필드 검증 함수 (모든 필수 필드를 순차적으로 검증)
const validateAll = (): boolean => {
  // 모든 validation 에러 초기화
  internalValidationErrors.value = {};
  
  // 법인일 때는 Contact 필드 validation 에러를 먼저 제거 (초기화 후 즉시)
  if (isCorporate.value) {
    delete internalValidationErrors.value.email;
    delete internalValidationErrors.value.mobileNoCountryCode;
    delete internalValidationErrors.value.mobileNo;
    delete internalValidationErrors.value.contactPostalCode;
    delete internalValidationErrors.value.contactForeignAddr1;
  }
  
  console.log('📋 [OfficerAppointmentForm] validateAll 시작', {
    eFilingMode: eFilingMode.value,
    isCorporate: isCorporate.value,
    categoryCd: contactData.value.CATEGORY_CD
  });
  
  // 순차적으로 검증 (첫 번째 실패 시 즉시 반환)
  const isEntityValid = validateEntity();
  if (!isEntityValid) {
    console.log('❌ [OfficerAppointmentForm] Entity 검증 실패');
    return false;
  }
  
  const isPositionValid = validatePosition();
  if (!isPositionValid) {
    console.log('❌ [OfficerAppointmentForm] Position 검증 실패');
    return false;
  }
  
  const isAppointedDateValid = validateAppointedDate();
  if (!isAppointedDateValid) {
    console.log('❌ [OfficerAppointmentForm] AppointedDate 검증 실패');
    return false;
  }
  
  const isOfficerNameValid = validateOfficerName();
  if (!isOfficerNameValid) {
    console.log('❌ [OfficerAppointmentForm] OfficerName 검증 실패');
    return false;
  }
  
  // E-Filing 모드이고 회사일 때 Corporate 필드 검증
  if (eFilingMode && isCorporate.value) {
    const isJurisdictionValid = validateJurisdiction();
    if (!isJurisdictionValid) {
      return false;
    }
    
    const isEntityNameValid = validateEntityName();
    if (!isEntityNameValid) {
      return false;
    }
    
    const isRegistrationNumberValid = validateRegistrationNumber();
    if (!isRegistrationNumberValid) {
      return false;
    }
    
    // 회사일 때도 Residential Address 필수 필드 검증
    const isResidentialPostalCodeValid = validateResidentialPostalCode();
    if (!isResidentialPostalCodeValid) {
      return false;
    }
    
    const isResidentialForeignAddr1Valid = validateResidentialForeignAddr1();
    if (!isResidentialForeignAddr1Valid) {
      return false;
    }
    
    // 법인일 때는 Contact 필드 validation 에러 제거
    delete internalValidationErrors.value.email;
    delete internalValidationErrors.value.mobileNoCountryCode;
    delete internalValidationErrors.value.mobileNo;
  }
  
  // E-Filing 모드이고 개인일 때 개인 필드 검증
  if (eFilingMode && isIndividual.value) {
    // Residential Address 필수 필드 검증
    const isResidentialPostalCodeValid = validateResidentialPostalCode();
    if (!isResidentialPostalCodeValid) {
      return false;
    }
    
    const isResidentialForeignAddr1Valid = validateResidentialForeignAddr1();
    if (!isResidentialForeignAddr1Valid) {
      return false;
    }
    
    // Contact Address 필수 필드 검증
    const isContactPostalCodeValid = validateContactPostalCode();
    if (!isContactPostalCodeValid) {
      return false;
    }
    
    const isContactForeignAddr1Valid = validateContactForeignAddr1();
    if (!isContactForeignAddr1Valid) {
      return false;
    }
    
    // Email 및 Mobile 검증
    const isContactEmailValid = validateContactEmail();
    if (!isContactEmailValid) {
      return false;
    }
    
    const isContactMobileNoCountryCodeValid = validateContactMobileNoCountryCode();
    if (!isContactMobileNoCountryCodeValid) {
      return false;
    }
    
    const isContactMobileNoValid = validateContactMobileNo();
    if (!isContactMobileNoValid) {
      return false;
    }
  }
  
  // E-Filing 모드일 때 추가 필수 필드 검증
  if (eFilingMode.value) {
    console.log('📋 [OfficerAppointmentForm] E-Filing 모드 검증 시작', {
      isCorporate: isCorporate.value,
      categoryCd: contactData.value.CATEGORY_CD
    });
    
    // 회사일 때 필수 필드 검증
    if (isCorporate.value) {
      console.log('📋 [OfficerAppointmentForm] 회사 검증 시작');
      const isJurisdictionValid = validateJurisdiction();
      if (!isJurisdictionValid) {
        return false;
      }
      
      const isEntityNameValid = validateEntityName();
      if (!isEntityNameValid) {
        return false;
      }
      
      const isRegistrationNumberValid = validateRegistrationNumber();
      if (!isRegistrationNumberValid) {
        return false;
      }
      
      // 법인일 때 Residential Address 섹션 검증
      const isResidentialPostalCodeValid = validateResidentialPostalCode();
      if (!isResidentialPostalCodeValid) {
        console.log('❌ [OfficerAppointmentForm] 법인 Residential Postal Code 검증 실패');
        return false;
      }
      
      const isResidentialForeignAddr1Valid = validateResidentialForeignAddr1();
      if (!isResidentialForeignAddr1Valid) {
        console.log('❌ [OfficerAppointmentForm] 법인 Residential Foreign Address Line 1 검증 실패');
        return false;
      }
    } else {
      // 개인일 때 필수 필드 검증
      console.log('📋 [OfficerAppointmentForm] 개인 검증 시작');
      
      // Residential Address 섹션 검증
      const isResidentialPostalCodeValid = validateResidentialPostalCode();
      if (!isResidentialPostalCodeValid) {
        console.log('❌ [OfficerAppointmentForm] Residential Postal Code 검증 실패');
        return false;
      }
      
      const isResidentialForeignAddr1Valid = validateResidentialForeignAddr1();
      if (!isResidentialForeignAddr1Valid) {
        console.log('❌ [OfficerAppointmentForm] Residential Foreign Address Line 1 검증 실패');
        return false;
      }
      
      // Contact Address 섹션 검증
      const isContactPostalCodeValid = validateContactPostalCode();
      if (!isContactPostalCodeValid) {
        console.log('❌ [OfficerAppointmentForm] Contact Postal Code 검증 실패');
        return false;
      }
      
      const isContactForeignAddr1Valid = validateContactForeignAddr1();
      if (!isContactForeignAddr1Valid) {
        console.log('❌ [OfficerAppointmentForm] Contact Foreign Address Line 1 검증 실패');
        return false;
      }
      
      // Contact 섹션 검증
      const isEmailValid = validateContactEmail();
      if (!isEmailValid) {
        console.log('❌ [OfficerAppointmentForm] Email 검증 실패');
        return false;
      }
      
      const isCountryCodeValid = validateContactMobileNoCountryCode();
      if (!isCountryCodeValid) {
        console.log('❌ [OfficerAppointmentForm] Country Code 검증 실패');
        return false;
      }
      
      const isMobileNoValid = validateContactMobileNo();
      if (!isMobileNoValid) {
        console.log('❌ [OfficerAppointmentForm] Mobile No 검증 실패');
        return false;
      }
      
      console.log('✅ [OfficerAppointmentForm] 개인 검증 통과');
    }
  }
  
  // 모든 검증 통과
  console.log('✅ [OfficerAppointmentForm] 모든 검증 통과');
  return true;
};

// ========================================
// doc_variable 매핑 관련 코드
// ========================================

// 문서 바인딩 변수 매핑 정보
const docVariableMapping = {
  entityName: 'CompanyName',
  entityRegNo: 'RegistrationNo',
  appointedDate: ['EffectiveDate', 'AppointmentDate'], // 여러 변수 매핑
  officerName: 'AppointOfficerName',
  officerAddress: 'AppointOfficerAddress',
  officerAlternateAddress: 'AppointOfficerAltenateAddress', // Contact Address (대체 주소)
  officerIdNo: 'AppointOfficerIdNo',
  officerNationality: 'AppointOfficerNationality',
  officerDateOfBirth: 'AppintOfficerDateOfBirth', // 원본 변수명 유지 (오타 포함)
  officerEmail: 'AppointOffierEmail', // 원본 변수명 유지 (오타 포함)
  officerContactNo: 'AppointOfficerContactNo',
  positionHeld: 'PositionHeld',
  clientNm: 'CSPName'
};

// Officer 주소 포맷팅 함수 (공통 함수 사용)
const formatOfficerAddress = (): string => {
  const addressInfo = {
    ADDRESS_TYPE_CD: contactData.value.ADDRESS_TYPE_CD || '',
    POSTAL_CODE: contactData.value.POSTAL_CODE || '',
    BLOCK_NO: contactData.value.BLOCK_NO || '',
    STREET_NAME: contactData.value.STREET_NAME || '',
    BUILDING_NAME: contactData.value.BUILDING_NAME || '',
    ADDR_LEVEL: contactData.value.ADDR_LEVEL || '',
    ADDR_UNIT: contactData.value.ADDR_UNIT || '',
    FOREIGN_ADDRESS_LINE_1: contactData.value.FOREIGN_ADDRESS_LINE_1 || '',
    FOREIGN_ADDRESS_LINE_2: contactData.value.FOREIGN_ADDRESS_LINE_2 || ''
  };
  const address = formatAddress(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// Contact Address (대체 주소) 포맷팅 함수 (공통 함수 사용)
const formatContactAddress = (): string => {
  const altType = contactAddressTypeCd.value;
  const addressInfo = {
    ADDRESS_TYPE_CD: altType,
    POSTAL_CODE: contactData.value.ALT_POSTAL_CODE || '',
    BLOCK_NO: contactData.value.ALT_BLOCK_NO || '',
    STREET_NAME: contactData.value.ALT_STREET_NAME || '',
    BUILDING_NAME: contactData.value.ALT_BUILDING_NAME || '',
    ADDR_LEVEL: contactData.value.ALT_ADDR_LEVEL || '',
    ADDR_UNIT: contactData.value.ALT_ADDR_UNIT || '',
    FOREIGN_ADDRESS_LINE_1: contactData.value.ALT_FOREIGN_ADDRESS_LINE_1 || '',
    FOREIGN_ADDRESS_LINE_2: contactData.value.ALT_FOREIGN_ADDRESS_LINE_2 || ''
  };
  const address = formatAddress(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// Country Code에서 숫자만 추출 (+ 포함)
const extractCountryCodeNumber = (countryCd: string): string => {
  if (!countryCd) return '';

  // countryOptions에서 해당 코드의 title 찾기
  const countryOption = countryOptions.value.find(opt => opt.value === countryCd);
  const countryName = countryOption?.title || '';

  // 국가명에서 +숫자 패턴 추출 (예: "Singapore (+65)" -> "+65")
  const match = countryName.match(/\(\+(\d+)\)/);
  if (match) {
    return `+${match[1]}`;
  }

  // B05 코드에서 직접 숫자 부분 추출 시도 (예: B05065 -> +65)
  const codeMatch = countryCd.match(/B05(\d+)/);
  if (codeMatch) {
    return `+${codeMatch[1]}`;
  }

  return '';
};

// 전화번호 포맷 (Country Code + Mobile No)
const formatContactNo = (): string => {
  const countryCode = extractCountryCodeNumber(contactData.value.COUNTRY_CD);
  const mobileNo = contactData.value.MOBILE_NO || '';

  if (countryCode && mobileNo) {
    return `${countryCode} ${mobileNo}`;
  }
  return mobileNo;
};

// 폼 데이터를 doc_variable 정보와 함께 반환하는 메서드
const getFormDataWithDocVariables = async () => {
  // 필드명(키)을 기준으로 그룹화
  const fieldMap = new Map<string, { value: any; docVariables: string[] }>();

  // 기존 doc_variables 먼저 추가 (기존 로직 유지)
  // entityName
  if (entityName.value) {
    fieldMap.set('entityName', {
      value: entityName.value,
      docVariables: [docVariableMapping.entityName]
    });
  }

  // entityRegNo
  if (entityRegNo.value) {
    fieldMap.set('entityRegNo', {
      value: entityRegNo.value,
      docVariables: [docVariableMapping.entityRegNo]
    });
  }

  // appointedDate (여러 변수 매핑) - "10 Dec 2025" 형식으로 변환
  const appointedDateVal = appointedDateValue.value || '';
  if (appointedDateVal) {
    const dateVars = Array.isArray(docVariableMapping.appointedDate)
      ? docVariableMapping.appointedDate
      : [docVariableMapping.appointedDate];
    fieldMap.set('appointedDate', {
      value: formatDocDate(appointedDateVal),
      docVariables: dateVars
    });
  }

  // officerName (실제 이름)
  const officerNameVal = contactData.value.CONTACT_NAME || '';
  if (officerNameVal) {
    fieldMap.set('officerName', {
      value: officerNameVal,
      docVariables: [docVariableMapping.officerName]
    });
  }

  // officerAddress
  const officerAddressVal = formatOfficerAddress();
  if (officerAddressVal) {
    fieldMap.set('officerAddress', {
      value: officerAddressVal,
      docVariables: [docVariableMapping.officerAddress]
    });
  }

  // officerAlternateAddress (Contact Address - 대체 주소)
  const officerAlternateAddressVal = formatContactAddress();
  if (officerAlternateAddressVal) {
    fieldMap.set('officerAlternateAddress', {
      value: officerAlternateAddressVal,
      docVariables: [docVariableMapping.officerAlternateAddress]
    });
  }

  // officerIdNo (Identification No.)
  const officerIdNoVal = contactData.value.CONTACT_REG_NO || '';
  if (officerIdNoVal) {
    fieldMap.set('officerIdNo', {
      value: officerIdNoVal,
      docVariables: [docVariableMapping.officerIdNo]
    });
  }

  // officerNationality
  const nationalityVal = nationalityDisplay.value || '';
  if (nationalityVal) {
    fieldMap.set('officerNationality', {
      value: nationalityVal,
      docVariables: [docVariableMapping.officerNationality]
    });
  }

  // officerDateOfBirth - "10 Dec 2025" 형식으로 변환
  const dateOfBirthVal = contactData.value.DATE_OF_BIRTH || '';
  if (dateOfBirthVal) {
    fieldMap.set('officerDateOfBirth', {
      value: formatDocDate(dateOfBirthVal),
      docVariables: [docVariableMapping.officerDateOfBirth]
    });
  }

  // officerEmail
  const emailVal = contactData.value.CONTACT_EMAIL || '';
  if (emailVal) {
    fieldMap.set('officerEmail', {
      value: emailVal,
      docVariables: [docVariableMapping.officerEmail]
    });
  }

  // officerContactNo (Country Code + Mobile No)
  const contactNoVal = formatContactNo();
  if (contactNoVal) {
    fieldMap.set('officerContactNo', {
      value: contactNoVal,
      docVariables: [docVariableMapping.officerContactNo]
    });
  }

  // positionHeld (선택된 포지션 이름)
  if (position.value) {
    const selectedPosition = positionOptions.value.find(opt => opt.value === position.value);
    const positionName = selectedPosition?.title || '';
    if (positionName) {
      fieldMap.set('positionHeld', {
        value: positionName,
        docVariables: [docVariableMapping.positionHeld]
      });
    }
  }

  // clientNm (CSP Name - 값은 CreateTaskForm.vue에서 채움)
  fieldMap.set('clientNm', {
    value: '', // CreateTaskForm.vue에서 실제 값으로 채워짐
    docVariables: [docVariableMapping.clientNm]
  });

  // Entity 관련 공통 데이터 조회 및 doc_variables 생성
  if (entity.value) {
    try {
      const entityData = await getEntityData(entity.value);
      
      // {#CompanyName} - entityName에 추가 (기존 필드와 병합)
      if (entityData.companyName) {
        const existingField = fieldMap.get('entityName');
        if (existingField) {
          // 기존 필드가 있으면 docVariables만 추가 (중복 제거)
          if (!existingField.docVariables.includes('CompanyName')) {
            existingField.docVariables.push('CompanyName');
          }
        } else {
          // 없으면 새로 추가
          fieldMap.set('entityName', {
            value: entityData.companyName,
            docVariables: ['CompanyName']
          });
        }
      }

      // {#RegistrationNo} - entityRegNo에 추가 (기존 필드와 병합)
      if (entityData.registrationNumber) {
        const existingField = fieldMap.get('entityRegNo');
        if (existingField) {
          // 기존 필드가 있으면 docVariables만 추가 (중복 제거)
          if (!existingField.docVariables.includes('RegistrationNo')) {
            existingField.docVariables.push('RegistrationNo');
          }
        } else {
          // 없으면 새로 추가
          fieldMap.set('entityRegNo', {
            value: entityData.registrationNumber,
            docVariables: ['RegistrationNo']
          });
        }
      }

      // {#IncorporatedDate} - DATE_OF_INCORPORATION 날짜 형식 (formatDocDate 사용)
      if (entityData.incorporationDate) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.incorporationDate;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('incorporatedDate', {
            value: formattedDate,
            docVariables: ['IncorporatedDate']
          });
          console.log('✅ [OfficerAppointmentForm] IncorporatedDate 추가:', {
            raw: entityData.incorporationDate,
            converted: dateToFormat,
            formatted: formattedDate
          });
        } else {
          console.warn('⚠️ [OfficerAppointmentForm] IncorporatedDate 포맷팅 실패:', {
            raw: entityData.incorporationDate,
            converted: dateToFormat
          });
        }
      } else {
        console.warn('⚠️ [OfficerAppointmentForm] incorporationDate 없음');
      }

      // {#CompanyAddress}
      if (entityData.companyAddress) {
        fieldMap.set('companyAddress', {
          value: entityData.companyAddress,
          docVariables: ['CompanyAddress']
        });
      }

      // {#FinancialYearEnd} - FINANCIAL_YEAR_END 날짜 형식 (formatDocDate 사용)
      if (entityData.financialYearEnd) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.financialYearEnd;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('financialYearEnd', {
            value: formattedDate,
            docVariables: ['FinancialYearEnd']
          });
          console.log('✅ [OfficerAppointmentForm] FinancialYearEnd 추가:', {
            raw: entityData.financialYearEnd,
            converted: dateToFormat,
            formatted: formattedDate
          });
        } else {
          console.warn('⚠️ [OfficerAppointmentForm] FinancialYearEnd 포맷팅 실패:', {
            raw: entityData.financialYearEnd,
            converted: dateToFormat
          });
        }
      } else {
        console.warn('⚠️ [OfficerAppointmentForm] financialYearEnd 없음');
      }

      // {#PrimaryActivityCode}
      if (entityData.primaryActivityCode) {
        fieldMap.set('primaryActivityCode', {
          value: entityData.primaryActivityCode,
          docVariables: ['PrimaryActivityCode']
        });
      }

      // {#PrimaryActivityDescription}
      if (entityData.primaryActivityDescription) {
        fieldMap.set('primaryActivityDescription', {
          value: entityData.primaryActivityDescription,
          docVariables: ['PrimaryActivityDescription']
        });
      }

      // {#SecondaryActivityCode}
      if (entityData.secondaryActivityCode) {
        fieldMap.set('secondaryActivityCode', {
          value: entityData.secondaryActivityCode,
          docVariables: ['SecondaryActivityCode']
        });
      }

      // {#SecondaryActivityDescription}
      if (entityData.secondaryActivityDescription) {
        fieldMap.set('secondaryActivityDescription', {
          value: entityData.secondaryActivityDescription,
          docVariables: ['SecondaryActivityDescription']
        });
      }

      // {#EntityJurisdiction}
      if (entityData.entityJurisdiction) {
        fieldMap.set('entityJurisdiction', {
          value: entityData.entityJurisdiction,
          docVariables: ['EntityJurisdiction']
        });
      }

      // {#EntityType}
      if (entityData.entityType) {
        fieldMap.set('entityType', {
          value: entityData.entityType,
          docVariables: ['EntityType']
        });
      }

      // {#CompanyEmail}
      if (entityData.companyEmail) {
        fieldMap.set('companyEmail', {
          value: entityData.companyEmail,
          docVariables: ['CompanyEmail']
        });
      }
    } catch (error) {
      console.error('❌ [OfficerAppointmentForm] Entity 데이터 조회 실패:', error);
    }
  }

  // 결과 객체 생성 - 각 필드를 { fieldName: { value: "...", doc_variables: ["#..."] } } 형태로 변환
  const result: Record<string, any> = {};

  fieldMap.forEach((item, fieldName) => {
    // doc_variables를 배열로 변환 (#접두사 추가)
    const docVariablesArray = item.docVariables.map(v => `#${v}`);

    // 각 필드를 { value: "...", doc_variables: ["#..."] } 형태의 객체로 저장
    result[fieldName] = {
      value: item.value,
      doc_variables: docVariablesArray
    };
  });

  console.log('📋 [OfficerAppointmentForm] getFormDataWithDocVariables:', result);
  return result;
};

// ========================================
// E-Filing JSON 생성 관련 코드
// ========================================

// Position 코드에서 BIGO1 값 가져오기
const getPositionBigo1 = (positionCode: string): string => {
  const positionItem = positionCodes.value.find(code => code.totalCode === positionCode);
  return positionItem?.bigo1 || '';
};

// Country 코드에서 BIGO3 값 가져오기 (+ 제거)
const getCountryBigo3WithoutPlus = (countryCd: string): string => {
  const countryItem = countryOptions.value.find(opt => opt.value === countryCd);
  const bigo3 = countryItem?.bigo3 || '';
  // + 기호 제거
  return bigo3.replace(/^\+/, '');
};

// E-Filing용 JSON 데이터 생성 함수 (async)
// 코드인 경우 { code: '코드값', name: '코드명' } 형태로 저장
// BaseCode에서 못 찾으면 API로 직접 조회
const getEFilingData = async (latestContactData?: any, existingEFilingData?: any) => {
  // 최신 contactData 사용 (파라미터로 전달된 경우) 또는 기존 contactData.value 사용
  // latestContactData가 전달된 경우 camelCase를 UPPER_SNAKE_CASE로 변환
  console.log('📋 [OfficerAppointmentForm] getEFilingData 호출:', {
    latestContactDataAlternateDirector: latestContactData?.alternateDirector,
    alternateDirectorDataValue: alternateDirectorData.value,
    position: position.value
  });
  
  let currentContactData = contactData.value;
  
  // 기존 eFiling 데이터에서 필수 필드 값 추출 (빈칸일 때 기존 값 유지용)
  const existingPositionHolder = existingEFilingData?.eFilingDataValidation?.AppointList?.[0]?.addPositionHolder || 
                                 existingEFilingData?.eFilingDataForApi?.AppointList?.[0]?.addPositionHolder || null;
  
  if (latestContactData) {
    // camelCase 필드를 UPPER_SNAKE_CASE로 변환
    currentContactData = {
      ...contactData.value, // 기존 데이터 유지
      CONTACT_REG_NO: latestContactData.contactRegNo || latestContactData.CONTACT_REG_NO || '',
      CONTACT_NAME: latestContactData.contactName || latestContactData.CONTACT_NAME || '',
      ID_TYPE_CD: latestContactData.idTypeCd || latestContactData.ID_TYPE_CD || '',
      NATIONALITY_CD: latestContactData.nationalityCd || latestContactData.NATIONALITY_CD || '',
      DATE_OF_BIRTH: latestContactData.dateOfBirth || latestContactData.DATE_OF_BIRTH || '',
      CATEGORY_CD: latestContactData.categoryCd || latestContactData.CATEGORY_CD || '',
      ADDRESS_TYPE_CD: latestContactData.addressTypeCd || latestContactData.ADDRESS_TYPE_CD || '',
      POSTAL_CODE: latestContactData.postalCode || latestContactData.POSTAL_CODE || '',
      BLOCK_NO: latestContactData.blockNo || latestContactData.BLOCK_NO || '',
      STREET_NAME: latestContactData.streetName || latestContactData.STREET_NAME || '',
      BUILDING_NAME: latestContactData.buildingName || latestContactData.BUILDING_NAME || '',
      ADDR_LEVEL: latestContactData.addrLevel || latestContactData.ADDR_LEVEL || '',
      ADDR_UNIT: latestContactData.addrUnit || latestContactData.ADDR_UNIT || '',
      FOREIGN_ADDRESS_LINE_1: latestContactData.foreignAddressLine1 || latestContactData.FOREIGN_ADDRESS_LINE_1 || '',
      FOREIGN_ADDRESS_LINE_2: latestContactData.foreignAddressLine2 || latestContactData.FOREIGN_ADDRESS_LINE_2 || '',
      ALT_POSTAL_CODE: latestContactData.altPostalCode || latestContactData.ALT_POSTAL_CODE || '',
      ALT_BLOCK_NO: latestContactData.altBlockNo || latestContactData.ALT_BLOCK_NO || '',
      ALT_STREET_NAME: latestContactData.altStreetName || latestContactData.ALT_STREET_NAME || '',
      ALT_BUILDING_NAME: latestContactData.altBuildingName || latestContactData.ALT_BUILDING_NAME || '',
      ALT_ADDR_LEVEL: latestContactData.altAddrLevel || latestContactData.ALT_ADDR_LEVEL || '',
      ALT_ADDR_UNIT: latestContactData.altAddrUnit || latestContactData.ALT_ADDR_UNIT || '',
      ALT_FOREIGN_ADDRESS_LINE_1: latestContactData.altForeignAddressLine1 || latestContactData.ALT_FOREIGN_ADDRESS_LINE_1 || '',
      ALT_FOREIGN_ADDRESS_LINE_2: latestContactData.altForeignAddressLine2 || latestContactData.ALT_FOREIGN_ADDRESS_LINE_2 || '',
      CONTACT_EMAIL: latestContactData.email || latestContactData.contactEmail || latestContactData.CONTACT_EMAIL || '',
      COUNTRY_CD: latestContactData.countryCd || latestContactData.COUNTRY_CD || '',
      AREA_CODE: latestContactData.mobileNoAreaCode || latestContactData.areaCode || latestContactData.AREA_CODE || '',
      MOBILE_NO: latestContactData.mobileNo || latestContactData.MOBILE_NO || '',
      JURISDICTION: latestContactData.jurisdiction || latestContactData.JURISDICTION || '',
      DATE_OF_INCORPORATION: latestContactData.dateOfIncorporation || latestContactData.incorporationDate || latestContactData.DATE_OF_INCORPORATION || '',
      REPRESENTATIVE_CD: latestContactData.representativeCd || latestContactData.REPRESENTATIVE_CD || ''
    };
  }
  
  // 세션에서 Client 정보 가져오기
  const customerInfo = sessionStore.getCustomerInfo();
  const filingAgentUEN = customerInfo?.regNo || '';

  // Position BIGO1 값
  const positionBigo1 = getPositionBigo1(position.value);

  // Country BIGO3 값 (+ 제거)
  const mobileNoCountryCode = getCountryBigo3WithoutPlus(currentContactData.COUNTRY_CD);

  // ID Type 코드명 (B03) - BIGO1에서 E-Filing용 name 가져오기
  const idTypeItem = identificationTypeOptions.value.find(opt => opt.value === currentContactData.ID_TYPE_CD);
  let idTypeName = idTypeItem?.bigo1 || idTypeItem?.title || '';
  // options에서 못 찾으면 API 조회
  if (!idTypeName && currentContactData.ID_TYPE_CD) {
    try {
      const response = await baseCodeApi.getCodeByTotalCode('CM', currentContactData.ID_TYPE_CD);
      idTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
    } catch (e) { console.warn('ID Type BIGO1 조회 실패:', e); }
  }

  // Nationality 코드명 (B04) - BIGO1에서 E-Filing용 name 가져오기
  const nationalityItem = nationalityOptions.value.find(opt => opt.value === currentContactData.NATIONALITY_CD);
  let nationalityName = nationalityItem?.bigo1 || nationalityItem?.title || '';
  if (!nationalityName && currentContactData.NATIONALITY_CD) {
    try {
      const response = await baseCodeApi.getCodeByTotalCode('CM', currentContactData.NATIONALITY_CD);
      nationalityName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
    } catch (e) { console.warn('Nationality BIGO1 조회 실패:', e); }
  }

  // Address Type 코드명 (B07) - BIGO1에서 E-Filing용 name 가져오기
  const addressTypeItem = addressTypeOptions.value.find(opt => opt.value === currentContactData.ADDRESS_TYPE_CD);
  let addressTypeName = addressTypeItem?.bigo1 || addressTypeItem?.title || '';
  if (!addressTypeName && currentContactData.ADDRESS_TYPE_CD) {
    try {
      const response = await baseCodeApi.getCodeByTotalCode('CM', currentContactData.ADDRESS_TYPE_CD);
      addressTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
    } catch (e) { console.warn('Address Type BIGO1 조회 실패:', e); }
  }

  const altAddressTypeCd = String(
    currentContactData.ALTERNATE_ADDRESS_CD || currentContactData.ADDRESS_TYPE_CD || 'B07001',
  ).trim();
  const altAddressTypeItem = addressTypeOptions.value.find((opt) => opt.value === altAddressTypeCd);
  let altAddressTypeName = altAddressTypeItem?.bigo1 || altAddressTypeItem?.title || '';
  if (!altAddressTypeName && altAddressTypeCd) {
    try {
      const response = await baseCodeApi.getCodeByTotalCode('CM', altAddressTypeCd);
      altAddressTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
    } catch (e) { console.warn('Alternate Address Type BIGO1 조회 실패:', e); }
  }

  // Entity Type 코드명 (A04) - BIGO1에서 E-Filing용 name 가져오기 - SG 국가코드 사용
  const companyTypeItem = companyTypeOptions.value.find(opt => opt.value === entityCompanyType.value);
  let companyTypeName = companyTypeItem?.bigo1 || companyTypeItem?.title || '';
  // options에서 못 찾으면 API 조회 (A04는 SG 국가코드 사용)
  if (!companyTypeName && entityCompanyType.value) {
    try {
      const response = await baseCodeApi.getCodeByTotalCode('SG', entityCompanyType.value);
      companyTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      console.log('📋 [OfficerAppointmentForm] Entity Type BIGO1 API 조회:', { code: entityCompanyType.value, bigo1: companyTypeName });
    } catch (e) { console.warn('Entity Type BIGO1 조회 실패:', e); }
  }

  // Officer Category 코드명 (B09) - L_GLOBAL_CONTACT에서 CATEGORY_CD 조회 후 BIGO3에서 E-Filing용 name 가져오기
  let officerCategoryCode = currentContactData.CATEGORY_CD || '';
  let contactCategoryName = '';
  
  // CATEGORY_CD가 없으면 CONTACT_CD로 L_GLOBAL_CONTACT에서 조회
  if (!officerCategoryCode && officerName.value && !isManualOfficerSentinel(officerName.value)) {
    try {
      const response = await contactsApi.getContactById(officerName.value);
      const contact = (response as any).contact || response.data?.contact;
      if (contact) {
        officerCategoryCode = contact.CATEGORY_CD || contact.categoryCd || '';
      }
    } catch (e) { 
      console.warn('Officer Category 조회 실패:', e); 
    }
  }
  
  // CATEGORY_CD가 있으면 BIGO3 조회 (E-Filing용 name)
  if (officerCategoryCode) {
    const contactCategoryItem = contactCategoryOptions.value.find(opt => opt.value === officerCategoryCode);
    contactCategoryName = contactCategoryItem?.bigo3 || contactCategoryItem?.title || '';
    if (!contactCategoryName) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', officerCategoryCode);
        contactCategoryName = (response as any).code?.bigo3 || (response as any).data?.code?.bigo3 || '';
      } catch (e) { console.warn('Officer Category BIGO3 조회 실패:', e); }
    }
  }

  // { code, name } 객체를 name 값으로 변환하는 함수
  const transformCodeNameToValue = (data: any): any => {
    if (data === null || data === undefined) {
      return data;
    }

    // 배열인 경우 각 요소를 재귀적으로 변환
    if (Array.isArray(data)) {
      return data.map(item => transformCodeNameToValue(item));
    }

    // 객체인 경우
    if (typeof data === 'object') {
      // { code, name } 형태인지 확인
      const keys = Object.keys(data);
      if (keys.length === 2 && keys.includes('code') && keys.includes('name')) {
        // name 값 반환 (name이 비어있으면 code 반환)
        return data.name || data.code || '';
      }

      // 일반 객체는 각 속성을 재귀적으로 변환
      const result: any = {};
      for (const key of keys) {
        result[key] = transformCodeNameToValue(data[key]);
      }
      return result;
    }

    // 기본형은 그대로 반환
    return data;
  };

  // 회사(CATEGORY_CD B09001) 여부 확인 - currentContactData.CATEGORY_CD를 직접 확인
  const categoryCd = currentContactData.CATEGORY_CD || officerCategoryCode || '';
  const isCorporateCategory = categoryCd === 'B09001';
  
  console.log('📋 [OfficerAppointmentForm] getEFilingData - Category check:', {
    categoryCd,
    officerCategoryCode,
    isCorporateCategory,
    contactDataCategoryCd: currentContactData.CATEGORY_CD,
    latestContactDataProvided: !!latestContactData
  });

  // Jurisdiction 코드명 (B15) - 회사일 때만 필요
  let jurisdictionName = '';
  if (isCorporateCategory && currentContactData.JURISDICTION) {
    const jurisdictionItem = jurisdictionOptions.value.find(opt => opt.TOTAL_CODE === currentContactData.JURISDICTION);
    jurisdictionName = jurisdictionItem?.CODE_NAME || '';
    if (!jurisdictionName) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', currentContactData.JURISDICTION);
        jurisdictionName = (response as any).code?.codeName || (response as any).data?.code?.codeName || '';
      } catch (e) { console.warn('Jurisdiction CODE_NAME 조회 실패:', e); }
    }
  }

  // 필수 필드가 빈칸일 때 기존 값 유지하는 헬퍼 함수
  const preserveRequiredField = (newValue: any, existingValue: any, fieldName: string): any => {
    // 새 값이 비어있고 기존 값이 있으면 기존 값 유지
    if ((!newValue || newValue === '') && existingValue) {
      console.log(`📋 [OfficerAppointmentForm] 필수 필드 ${fieldName} 빈칸 감지, 기존 값 유지:`, existingValue);
      return existingValue;
    }
    return newValue;
  };

  // 기존 값에서 필수 필드 추출 (빈칸 체크용)
  const getExistingValue = (path: string): any => {
    if (!existingPositionHolder) return null;
    const keys = path.split('.');
    let value = existingPositionHolder;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }
    return value;
  };

  // E-Filing Validation용 JSON 구조 ({ code, name } 형태)
  // addPositionHolder를 별도로 생성 (async 작업 포함)
  const createAddPositionHolder = async () => {
    // B30024 (Alternate Director)일 때 Alternate Director 정보 미리 조회
    // 항상 최신 alternateDirectorData.value를 직접 사용 (항상 최신 상태 보장)
    let alternateDirectorInfo: any = null;
    // 항상 최신 alternateDirectorData.value를 직접 사용
    const currentAlternateDirector = alternateDirectorData.value;
    
    console.log('📋 [OfficerAppointmentForm] createAddPositionHolder - Alternate Director 정보:', {
      latestContactDataAlternateDirector: latestContactData?.alternateDirector,
      alternateDirectorDataValue: alternateDirectorData.value,
      currentAlternateDirector,
      position: position.value,
      hasEndDate: currentAlternateDirector?.hasEndDate,
      endDate: currentAlternateDirector?.endDate
    });
    
    if (position.value === 'B30024' && currentAlternateDirector?.directorCd) {
      try {
        // Director Contact 정보 조회 (PROFILE_ID 가져오기)
        const directorContactResponse = await contactsApi.getContactById(currentAlternateDirector.directorCd);
        const directorContact = (directorContactResponse as any).contact || directorContactResponse.data?.contact;
        const directorProfileId = directorContact?.PROFILE_ID || directorContact?.profileId || '';

        // Director 이름 가져오기 (directorOptions에서 찾기)
        const directorOption = directorOptions.value.find(opt => opt.value === currentAlternateDirector.directorCd);
        const directorName = directorOption?.title || directorContact?.CONTACT_NAME || directorContact?.contactName || '';

        // startDate 포맷팅 (YYYY-MM-DD)
        const startDateValue = currentAlternateDirector.startDate || '';
        let formattedStartDate = '';
        if (startDateValue) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(startDateValue)) {
            formattedStartDate = startDateValue;
          } else {
            try {
              const date = new Date(startDateValue);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                formattedStartDate = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.warn('Failed to parse startDate:', e);
            }
          }
        }

        // endDate 포맷팅 (hasEndDate가 true이고 endDate가 null이 아니고 빈 문자열이 아닐 때만)
        let formattedEndDate = '';
        if (currentAlternateDirector.hasEndDate && currentAlternateDirector.endDate && currentAlternateDirector.endDate !== null && currentAlternateDirector.endDate !== '') {
          const endDateValue = currentAlternateDirector.endDate;
          if (/^\d{4}-\d{2}-\d{2}$/.test(endDateValue)) {
            formattedEndDate = endDateValue;
          } else {
            try {
              const date = new Date(endDateValue);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                formattedEndDate = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.warn('Failed to parse endDate:', e);
            }
          }
        }

        // hasEndDate가 true이고 formattedEndDate가 있으면 endDate 값 설정, 없으면 null
        alternateDirectorInfo = {
          directorPeopleProfileId: directorProfileId,
          directorName: directorName,
          startDate: formattedStartDate,
          hasEndDate: currentAlternateDirector.hasEndDate === true, // 명시적으로 boolean으로 설정
          endDate: (currentAlternateDirector.hasEndDate === true && formattedEndDate) ? formattedEndDate : null // 항상 endDate 필드 포함
        };
        
        console.log('📋 [OfficerAppointmentForm] createAddPositionHolder - 생성된 alternateDirectorInfo:', {
          ...alternateDirectorInfo,
          '원본 hasEndDate': currentAlternateDirector.hasEndDate,
          '원본 endDate': currentAlternateDirector.endDate,
          'formattedEndDate': formattedEndDate
        });
      } catch (error) {
        console.error('❌ Failed to fetch director contact for addAlternateDirectors:', error);
      }
    }

    // AUDITOR인지 확인
    const isAuditor = position.value === 'B30025' || positionBigo1 === 'LC_AUD';
    
    // AUDITOR인 경우 특별한 구조로 생성
    if (isAuditor && isCorporateCategory) {
      const jurisdictionCd = currentContactData.JURISDICTION || '';
      const isSingaporeJurisdiction = jurisdictionCd === 'B15201';
      
      // entryDate 포맷팅
      const entryDateValue = (() => {
        const dateValue = preserveRequiredField(
          appointedDateValue.value || '',
          getExistingValue('addPositionHeld.0.entryDate'),
          'entryDate'
        );
        
        if (dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
          return dateValue;
        }
        
        if (dateValue) {
          try {
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          } catch (e) {
            console.warn('Failed to parse entryDate:', e);
          }
        }
        
        return dateValue || '';
      })();
      
      // 기본 AUDITOR 구조
      // eFilingDataValidation에서는 officerCategory를 객체 형태로 유지
      // eFilingDataForApi에서는 transformCodeNameToValue로 문자열로 변환됨
      const auditorPositionHolder: any = {
        registeredLocally: isSingaporeJurisdiction ? 'YES' : 'NO',
        positionHolderSource: isSingaporeJurisdiction 
          ? 'CORPORATE_LOCAL_ENTITY' 
          : 'UNREGISTERED_FOREIGN_ENTITY',
        officerCategory: {
          code: officerCategoryCode,
          name: contactCategoryName
        },
        registerAction: 'ADD',
        addPositionHeld: [
          {
            positionHeld: positionBigo1,
            entryDate: entryDateValue
          }
        ],
        registrationIdNo: preserveRequiredField(
          currentContactData.CONTACT_REG_NO || '',
          getExistingValue('registrationIdNo'),
          'registrationIdNo'
        ),
        name: preserveRequiredField(
          currentContactData.CONTACT_NAME || '',
          getExistingValue('name'),
          'name'
        )
      };
      
      if (isSingaporeJurisdiction) {
        // 싱가포르 회사: Local Address 필드만 포함
        auditorPositionHolder.postalCode = currentContactData.POSTAL_CODE || '';
        auditorPositionHolder.levelNo = currentContactData.ADDR_LEVEL || '';
        auditorPositionHolder.unitNo = currentContactData.ADDR_UNIT || '';
        auditorPositionHolder.blkhseNo = currentContactData.BLOCK_NO || '';
        auditorPositionHolder.streetName = currentContactData.STREET_NAME || '';
        auditorPositionHolder.buildingName = currentContactData.BUILDING_NAME || '';
      } else {
        // 외국 회사: Foreign Address 필드와 email, incorpCountry 포함
        auditorPositionHolder.email = currentContactData.CONTACT_EMAIL || '';
        
        // Jurisdiction BIGO1 값 조회
        let jurisdictionBigo1 = '';
        if (jurisdictionCd) {
          const jurisdictionItem = jurisdictionOptions.value.find(opt => opt.TOTAL_CODE === jurisdictionCd);
          if (jurisdictionItem) {
            try {
              const response = await baseCodeApi.getCodeByTotalCode('CM', jurisdictionCd);
              jurisdictionBigo1 = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
            } catch (e) { 
              console.warn('Jurisdiction BIGO1 조회 실패:', e); 
            }
          }
        }
        auditorPositionHolder.incorpCountry = jurisdictionBigo1;
        auditorPositionHolder.foreignAddr1 = currentContactData.FOREIGN_ADDRESS_LINE_1 || '';
        auditorPositionHolder.foreignAddr2 = currentContactData.FOREIGN_ADDRESS_LINE_2 || '';
      }
      
      return auditorPositionHolder;
    }
    
    // AUDITOR가 아닌 경우 기본 구조 생성
    const basePositionHolder: any = {
      registrationIdNo: preserveRequiredField(
        currentContactData.CONTACT_REG_NO || '',
        getExistingValue('registrationIdNo'),
        'registrationIdNo'
      ),
      idType: {
        code: preserveRequiredField(
          currentContactData.ID_TYPE_CD || '',
          getExistingValue('idType.code'),
          'idType.code'
        ),
        name: preserveRequiredField(
          idTypeName,
          getExistingValue('idType.name'),
          'idType.name'
        )
      },
      name: preserveRequiredField(
        currentContactData.CONTACT_NAME || '',
        getExistingValue('name'),
        'name'
      ),
      registerAction: 'ADD',
      officerCategory: {
        code: officerCategoryCode,
        name: contactCategoryName
      },
      addPositionHeld: [
        (() => {
          const positionHeldObj: any = {
            positionHeld: positionBigo1,
            entryDate: (() => {
              const dateValue = preserveRequiredField(
                appointedDateValue.value || '',
                getExistingValue('addPositionHeld.0.entryDate'),
                'entryDate'
              );
              
              if (dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
              }
              
              if (dateValue) {
                try {
                  const date = new Date(dateValue);
                  if (!isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  }
                } catch (e) {
                  console.warn('Failed to parse entryDate:', e);
                }
              }
              
              return dateValue || '';
            })()
          };

          // B30024 (Alternate Director)일 때만 addAlternateDirectors 추가
          if (alternateDirectorInfo) {
            positionHeldObj.addAlternateDirectors = [
              {
                directorPeopleProfileId: alternateDirectorInfo.directorPeopleProfileId,
                directorName: alternateDirectorInfo.directorName,
                startDate: alternateDirectorInfo.startDate,
                hasEndDate: alternateDirectorInfo.hasEndDate,
                endDate: alternateDirectorInfo.endDate !== undefined ? alternateDirectorInfo.endDate : null // 항상 endDate 필드 포함
              }
            ];
          }

          return positionHeldObj;
        })()
      ],
      nationality: {
        code: preserveRequiredField(
          currentContactData.NATIONALITY_CD || '',
          getExistingValue('nationality.code'),
          'nationality.code'
        ),
        name: preserveRequiredField(
          nationalityName,
          getExistingValue('nationality.name'),
          'nationality.name'
        )
      },
      profileId: '',
      birthDate: formatBirthDateForRequestPayload(currentContactData.DATE_OF_BIRTH),
      hasBirthDate: !!String(currentContactData.DATE_OF_BIRTH || '').trim(),
      addressType: {
        code: currentContactData.ADDRESS_TYPE_CD || '',
        name: addressTypeName
      },
      // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
      blkhseNo: (currentContactData.ADDRESS_TYPE_CD === 'B07001') ? (currentContactData.BLOCK_NO || '') : '',
      streetName: (currentContactData.ADDRESS_TYPE_CD === 'B07001') ? (currentContactData.STREET_NAME || '') : '',
      buildingName: (currentContactData.ADDRESS_TYPE_CD === 'B07001') ? (currentContactData.BUILDING_NAME || '') : '',
      levelNo: (currentContactData.ADDRESS_TYPE_CD === 'B07001') ? (currentContactData.ADDR_LEVEL || '') : '',
      unitNo: (currentContactData.ADDRESS_TYPE_CD === 'B07001') ? (currentContactData.ADDR_UNIT || '') : '',
      postalCode: (currentContactData.ADDRESS_TYPE_CD === 'B07001') ? (currentContactData.POSTAL_CODE || '') : '',
      foreignAddr1: (currentContactData.ADDRESS_TYPE_CD === 'B07002') ? (currentContactData.FOREIGN_ADDRESS_LINE_1 || '') : '',
      foreignAddr2: (currentContactData.ADDRESS_TYPE_CD === 'B07002') ? (currentContactData.FOREIGN_ADDRESS_LINE_2 || '') : ''
    };

    // 회사일 때만 jurisdiction 추가 (필수 필드)
    if (isCorporateCategory) {
      basePositionHolder.jurisdiction = preserveRequiredField(
        currentContactData.JURISDICTION || '',
        getExistingValue('jurisdiction'),
        'jurisdiction'
      );
    }

    // 개인(B09002)일 때만 Contact 관련 필드 추가 (필수 필드들)
    if (!isCorporateCategory) {
      basePositionHolder.contactAddressType = {
        code: altAddressTypeCd,
        name: altAddressTypeName
      };
      basePositionHolder.contactBlkhseNo = (altAddressTypeCd === 'B07001') ? (currentContactData.ALT_BLOCK_NO || '') : '';
      basePositionHolder.contactStreetName = (altAddressTypeCd === 'B07001') ? (currentContactData.ALT_STREET_NAME || '') : '';
      basePositionHolder.contactBuildingName = (altAddressTypeCd === 'B07001') ? (currentContactData.ALT_BUILDING_NAME || '') : '';
      basePositionHolder.contactLevelNo = (altAddressTypeCd === 'B07001') ? (currentContactData.ALT_ADDR_LEVEL || '') : '';
      basePositionHolder.contactUnitNo = (altAddressTypeCd === 'B07001') ? (currentContactData.ALT_ADDR_UNIT || '') : '';
      basePositionHolder.contactPostalCode = preserveRequiredField(
        (altAddressTypeCd === 'B07001') ? (currentContactData.ALT_POSTAL_CODE || '') : '',
        getExistingValue('contactPostalCode'),
        'contactPostalCode'
      );
      basePositionHolder.contactForeignAddr1 = preserveRequiredField(
        (altAddressTypeCd === 'B07002') ? (currentContactData.ALT_FOREIGN_ADDRESS_LINE_1 || '') : '',
        getExistingValue('contactForeignAddr1'),
        'contactForeignAddr1'
      );
      basePositionHolder.contactForeignAddr2 = (altAddressTypeCd === 'B07002') ? (currentContactData.ALT_FOREIGN_ADDRESS_LINE_2 || '') : '';
      basePositionHolder.email = preserveRequiredField(
        currentContactData.CONTACT_EMAIL || '',
        getExistingValue('email'),
        'email'
      );
      basePositionHolder.mobileNoCountryCode = preserveRequiredField(
        mobileNoCountryCode,
        getExistingValue('mobileNoCountryCode'),
        'mobileNoCountryCode'
      );
      basePositionHolder.mobileNoAreaCode = currentContactData.AREA_CODE || '';
      basePositionHolder.mobileNo = preserveRequiredField(
        currentContactData.MOBILE_NO || '',
        getExistingValue('mobileNo'),
        'mobileNo'
      );
    }

    return basePositionHolder;
  };

  // addPositionHolder 생성 (await 사용)
  const addPositionHolder = await createAddPositionHolder();

  const eFilingDataValidation = {
    AppointList: [
      {
        entityType: {
          code: entityCompanyType.value,
          name: companyTypeName
        },
        addPositionHolder
      }
    ],
    EntityUEN: preserveRequiredField(
      entityRegNo.value || '',
      existingEFilingData?.eFilingDataValidation?.EntityUEN || existingEFilingData?.eFilingDataForApi?.EntityUEN || '',
      'EntityUEN'
    ),
    FilingAgentUEN: filingAgentUEN,
    entityName: preserveRequiredField(
      entityName.value || '',
      existingEFilingData?.eFilingDataValidation?.entityName || existingEFilingData?.eFilingDataForApi?.entityName || '',
      'entityName'
    )
  };

  // 회사(B09001)일 때 Contact 관련 필드 제거 함수
  // categoryCd를 직접 사용하여 회사 여부 확인
  const removeCorporateContactFields = (data: any, useCategoryCd: string = '') => {
    if (!data || !data.AppointList || !Array.isArray(data.AppointList)) {
      return data;
    }

    data.AppointList.forEach((appointItem: any) => {
      if (appointItem.addPositionHolder) {
        let isCorporate = false;
        
        // categoryCd가 제공되면 직접 사용
        if (useCategoryCd === 'B09001') {
          isCorporate = true;
        } else {
          // categoryCd가 없으면 officerCategory에서 확인
          const officerCategory = appointItem.addPositionHolder.officerCategory;
          const categoryCode = typeof officerCategory === 'object' && officerCategory !== null
            ? officerCategory.code
            : officerCategory;
          isCorporate = categoryCode === 'B09001';
        }
        
        if (isCorporate) {
          // Contact 관련 필드 제거
          delete appointItem.addPositionHolder.contactAddressType;
          delete appointItem.addPositionHolder.contactBlkhseNo;
          delete appointItem.addPositionHolder.contactStreetName;
          delete appointItem.addPositionHolder.contactBuildingName;
          delete appointItem.addPositionHolder.contactLevelNo;
          delete appointItem.addPositionHolder.contactUnitNo;
          delete appointItem.addPositionHolder.contactPostalCode;
          delete appointItem.addPositionHolder.contactForeignAddr1;
          delete appointItem.addPositionHolder.contactForeignAddr2;
          delete appointItem.addPositionHolder.email;
          delete appointItem.addPositionHolder.mobileNoCountryCode;
          delete appointItem.addPositionHolder.mobileNoAreaCode;
          delete appointItem.addPositionHolder.mobileNo;
        }
      }
    });

    return data;
  };

  // 회사일 때 Contact 필드 제거 (혹시 모를 경우를 대비해 한 번 더 제거)
  const cleanedEFilingDataValidation = removeCorporateContactFields(JSON.parse(JSON.stringify(eFilingDataValidation)), categoryCd);

  // E-Filing API용 JSON 구조 (name 값만)
  let eFilingDataForApi = transformCodeNameToValue(cleanedEFilingDataValidation);

  // 모든 직책(AUDITOR 포함) addPositionHolder 구조 유지
  // eFilingDataForApi에서도 Contact 필드 제거 (categoryCd 전달)
  eFilingDataForApi = removeCorporateContactFields(JSON.parse(JSON.stringify(eFilingDataForApi)), categoryCd);
  
  const cleanedEFilingDataForApi = eFilingDataForApi;

  console.log('📋 [OfficerAppointmentForm] getEFilingData:', {
    isCorporateCategory,
    categoryCd,
    officerCategoryCode,
    eFilingDataValidation: cleanedEFilingDataValidation,
    eFilingDataForApi: cleanedEFilingDataForApi,
    entityName: entityName.value
  });

  return {
    eFilingDataValidation: cleanedEFilingDataValidation,
    eFilingDataForApi: cleanedEFilingDataForApi
  };
};

function officerFormDataHasManualProfile(fd: Record<string, any>): boolean {
  if (!fd || typeof fd !== 'object') return false;
  const linked = String(fd.officerNameCd ?? fd.contactCd ?? fd.officerCode ?? '').trim();
  if (linked) return false;
  if (fd.contactData && typeof fd.contactData === 'object') {
    const cd = fd.contactData as Record<string, any>;
    const meaningful = Object.keys(cd).some((k) => {
      const val = cd[k];
      if (val === null || val === undefined) return false;
      if (typeof val === 'string' && val.trim() !== '') return true;
      if (typeof val === 'boolean') return true;
      if (typeof val === 'number') return true;
      return false;
    });
    if (meaningful) return false;
  }
  const name = String(fd.directorName ?? fd.officerName ?? '').trim();
  return !!name;
}

function applyContactNamePartsFromFullName() {
  const full = String(contactData.value.CONTACT_NAME || '').trim();
  if (!full) return;
  const parts = splitContactDisplayName(full);
  const first = String(contactData.value.FIRST_NAME || '').trim();
  const last = String(contactData.value.SURNAME || '').trim();
  const middle = String(contactData.value.MIDDLE_NAME || '').trim();
  const looksWrongSplit = first && last && first === last && !middle;
  if (looksWrongSplit || (!first && !last)) {
    contactData.value.FIRST_NAME = parts.firstName;
    contactData.value.MIDDLE_NAME = parts.middleName;
    contactData.value.SURNAME = parts.surname;
    return;
  }
  if (!first) contactData.value.FIRST_NAME = parts.firstName;
  if (!middle) contactData.value.MIDDLE_NAME = parts.middleName;
  if (!last) contactData.value.SURNAME = parts.surname;
}

function hydrateContactDataFromFlatOfficerForm(fd: Record<string, any>): void {
  const built = buildOfficerAppointmentContactDataFromFlat(fd) as Record<string, any>;
  contactData.value = {
    CONTACT_NAME: built.CONTACT_NAME || built.contactName || '',
    FIRST_NAME: built.FIRST_NAME || built.firstName || '',
    MIDDLE_NAME: built.MIDDLE_NAME || built.middleName || '',
    SURNAME: built.SURNAME || built.surname || '',
    ID_TYPE_CD: built.ID_TYPE_CD || built.idTypeCd || '',
    CONTACT_REG_NO: built.CONTACT_REG_NO || built.contactRegNo || '',
    NATIONALITY_CD: built.NATIONALITY_CD || built.nationalityCd || '',
    DATE_OF_BIRTH: (built.DATE_OF_BIRTH || built.dateOfBirth || '').substring(0, 10),
    CATEGORY_CD: built.CATEGORY_CD || built.categoryCd || 'B09002',
    JURISDICTION: built.JURISDICTION || built.jurisdiction || '',
    AUDITOR_YN: 'No',
    DATE_OF_INCORPORATION: '',
    REPRESENTATIVE_CD: '',
    ADDRESS_TYPE_CD: String(built.ADDRESS_TYPE_CD || built.addressTypeCd || '').trim() || 'B07001',
    POSTAL_CODE: built.POSTAL_CODE || built.postalCode || '',
    BLOCK_NO: built.BLOCK_NO || built.blockNo || '',
    STREET_NAME: built.STREET_NAME || built.streetName || '',
    BUILDING_NAME: built.BUILDING_NAME || built.buildingName || '',
    ADDR_LEVEL: built.ADDR_LEVEL || built.addrLevel || '',
    ADDR_UNIT: built.ADDR_UNIT || built.addrUnit || '',
    FOREIGN_ADDRESS_LINE_1: built.FOREIGN_ADDRESS_LINE_1 || built.foreignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: built.FOREIGN_ADDRESS_LINE_2 || built.foreignAddressLine2 || '',
    ALTERNATE_ADDRESS_CD: String(built.ALTERNATE_ADDRESS_CD || built.alternateAddressCd || '').trim(),
    ALT_POSTAL_CODE: built.ALT_POSTAL_CODE || built.altPostalCode || '',
    ALT_BLOCK_NO: built.ALT_BLOCK_NO || built.altBlockNo || '',
    ALT_STREET_NAME: built.ALT_STREET_NAME || built.altStreetName || '',
    ALT_BUILDING_NAME: built.ALT_BUILDING_NAME || built.altBuildingName || '',
    ALT_ADDR_LEVEL: built.ALT_ADDR_LEVEL || built.altAddrLevel || '',
    ALT_ADDR_UNIT: built.ALT_ADDR_UNIT || built.altAddrUnit || '',
    ALT_FOREIGN_ADDRESS_LINE_1: built.ALT_FOREIGN_ADDRESS_LINE_1 || built.altForeignAddressLine1 || '',
    ALT_FOREIGN_ADDRESS_LINE_2: built.ALT_FOREIGN_ADDRESS_LINE_2 || built.altForeignAddressLine2 || '',
    COUNTRY_CD: built.COUNTRY_CD || built.countryCd || '',
    AREA_CODE: built.AREA_CODE || built.areaCode || '',
    MOBILE_NO: built.MOBILE_NO || built.mobileNo || '',
    CONTACT_EMAIL: built.CONTACT_EMAIL || built.contactEmail || '',
  };
  applyContactNamePartsFromFullName();
  normalizeSelectBoundFields();
}

/** FORM_DATA 에 이미 주소 스냅샷이 있으면 officer 설정 직후 DB getContactById 가 덮어쓰지 않도록 */
function shouldSkipOfficerDbFetchAfterLoadingForm(formData: any): boolean {
  if (!formData?.officerNameCd || !formData.contactData || typeof formData.contactData !== 'object') return false;
  const c = formData.contactData as Record<string, unknown>;
  const str = (v: unknown) => String(v ?? '').trim();
  if (str(c.ID_TYPE_CD ?? c.idTypeCd)) return true;
  if (str(c.COUNTRY_CD ?? c.countryCd)) return true;
  if (str(c.FIRST_NAME ?? c.firstName)) return true;
  const addr = String(c.ADDRESS_TYPE_CD ?? c.addressTypeCd ?? '').trim();
  const alt = String(c.ALTERNATE_ADDRESS_CD ?? c.alternateAddressCd ?? '').trim();
  const f1 = String(c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1 ?? '').trim();
  const f2 = String(c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2 ?? '').trim();
  const a1 = String(c.ALT_FOREIGN_ADDRESS_LINE_1 ?? c.altForeignAddressLine1 ?? '').trim();
  const a2 = String(c.ALT_FOREIGN_ADDRESS_LINE_2 ?? c.altForeignAddressLine2 ?? '').trim();
  return (
    addr === 'B07001' ||
    addr === 'B07002' ||
    alt === 'B07001' ||
    alt === 'B07002' ||
    !!f1 ||
    !!f2 ||
    !!a1 ||
    !!a2
  );
}

// 외부에서 폼 데이터 설정 (E-Filing 페이지에서 TASK_DATA로 데이터 채우기용)
const setFormData = async (formData: any) => {
  if (!formData) return;

  console.log('📋 [OfficerAppointmentForm] setFormData:', formData);

  // 데이터 복원 모드 시작
  isRestoringData.value = true;

  try {
    // Entity 설정
    if (formData.entity) {
      entity.value = formData.entity;
      // Entity 설정 후 Vue 반응성 시스템이 처리될 때까지 대기
      await nextTick();
    }

    // Entity 이름/등록번호 설정
    if (formData.entityName?.value) {
      entityName.value = formData.entityName.value;
    }
    if (formData.entityRegNo?.value) {
      entityRegNo.value = formData.entityRegNo.value;
    }

    // Position 설정 (entity watcher가 완료된 후)
    if (formData.position) {
      position.value = formData.position;
      // Position 설정 후 Vue 반응성 시스템이 처리될 때까지 대기
      await nextTick();

      // Position 변경 시 officers 로드가 완료될 때까지 약간 대기
      // (loadOfficers는 async 함수이므로 watcher에서 호출됨)
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Contact Data 설정 (officerName 설정 전에 먼저 설정하여 watch(officerName)가 DB 조회하지 않도록)
    if (formData.contactData) {
      const cd = formData.contactData;
      
      // AUDITOR_YN 처리 (boolean 또는 'Yes'/'No' 문자열)
      const auditorYnValue = cd.auditorYn !== undefined 
        ? (cd.auditorYn === true || cd.auditorYn === 1 || cd.auditorYn === 'Yes' ? 'Yes' : 'No')
        : (cd.AUDITOR_YN !== undefined
          ? (cd.AUDITOR_YN === true || cd.AUDITOR_YN === 1 || cd.AUDITOR_YN === 'Yes' ? 'Yes' : 'No')
          : (cd.AUDITTING !== undefined
            ? (cd.AUDITTING === 1 || cd.AUDITTING === true ? 'Yes' : 'No')
            : 'No'));
      
      contactData.value = {
        CONTACT_NAME: cd.contactName || cd.CONTACT_NAME || '',
        FIRST_NAME: cd.firstName || cd.FIRST_NAME || '',
        MIDDLE_NAME: cd.middleName || cd.MIDDLE_NAME || '',
        SURNAME: cd.surname || cd.SURNAME || '',
        ID_TYPE_CD: cd.idTypeCd || cd.ID_TYPE_CD || '',
        CONTACT_REG_NO: cd.contactRegNo || cd.CONTACT_REG_NO || '',
        NATIONALITY_CD: cd.nationalityCd || cd.NATIONALITY_CD || '',
        DATE_OF_BIRTH: (cd.dateOfBirth || cd.DATE_OF_BIRTH || cd.date_of_birth || cd.DOB || '').substring(0, 10),
        CATEGORY_CD: cd.categoryCd || cd.CATEGORY_CD || '', // E-Filing officerCategory용 (B09001: Corporate, B09002: Individual)
        // Corporate 필드 (CATEGORY_CD = 'B09001')
        JURISDICTION: cd.jurisdiction || cd.JURISDICTION || '',
        AUDITOR_YN: auditorYnValue,
        DATE_OF_INCORPORATION: (cd.dateOfIncorporation || cd.DATE_OF_INCORPORATION || cd.incorporationDate || '').substring(0, 10),
        REPRESENTATIVE_CD: cd.representativeCd || cd.REPRESENTATIVE_CD || cd.representativeName || '',
        ADDRESS_TYPE_CD: String(cd.ADDRESS_TYPE_CD || cd.addressTypeCd || '').trim() || 'B07001',
        POSTAL_CODE: cd.postalCode || cd.POSTAL_CODE || '',
        BLOCK_NO: cd.blockNo || cd.BLOCK_NO || '',
        STREET_NAME: cd.streetName || cd.STREET_NAME || '',
        BUILDING_NAME: cd.buildingName || cd.BUILDING_NAME || '',
        ADDR_LEVEL: cd.addrLevel || cd.ADDR_LEVEL || '',
        ADDR_UNIT: cd.addrUnit || cd.ADDR_UNIT || '',
        FOREIGN_ADDRESS_LINE_1: cd.foreignAddressLine1 || cd.FOREIGN_ADDRESS_LINE_1 || '',
        FOREIGN_ADDRESS_LINE_2: cd.foreignAddressLine2 || cd.FOREIGN_ADDRESS_LINE_2 || '',
        ALTERNATE_ADDRESS_CD: String(cd.ALTERNATE_ADDRESS_CD || cd.alternateAddressCd || '').trim(),
        ALT_POSTAL_CODE: cd.altPostalCode || cd.ALT_POSTAL_CODE || '',
        ALT_BLOCK_NO: cd.altBlockNo || cd.ALT_BLOCK_NO || '',
        ALT_STREET_NAME: cd.altStreetName || cd.ALT_STREET_NAME || '',
        ALT_BUILDING_NAME: cd.altBuildingName || cd.ALT_BUILDING_NAME || '',
        ALT_ADDR_LEVEL: cd.altAddrLevel || cd.ALT_ADDR_LEVEL || '',
        ALT_ADDR_UNIT: cd.altAddrUnit || cd.ALT_ADDR_UNIT || '',
        ALT_FOREIGN_ADDRESS_LINE_1: cd.altForeignAddressLine1 || cd.ALT_FOREIGN_ADDRESS_LINE_1 || '',
        ALT_FOREIGN_ADDRESS_LINE_2: cd.altForeignAddressLine2 || cd.ALT_FOREIGN_ADDRESS_LINE_2 || '',
        COUNTRY_CD: cd.countryCd || cd.COUNTRY_CD || '',
        AREA_CODE: cd.areaCode || cd.AREA_CODE || '',
        MOBILE_NO: cd.mobileNo || cd.MOBILE_NO || '',
        CONTACT_EMAIL: cd.contactEmail || cd.CONTACT_EMAIL || ''
      };
      const flatBuilt = buildOfficerAppointmentContactDataFromFlat(formData) as Record<string, any>;
      const pickFlat = (...keys: string[]) => {
        for (const k of keys) {
          const v = String(flatBuilt[k] ?? '').trim();
          if (v) return v;
        }
        return '';
      };
      const mergeIfEmpty = (field: string, ...flatKeys: string[]) => {
        if (String((contactData.value as any)[field] || '').trim()) return;
        const v = pickFlat(...flatKeys);
        if (v) (contactData.value as any)[field] = v;
      };
      mergeIfEmpty('ID_TYPE_CD', 'ID_TYPE_CD', 'idTypeCd');
      mergeIfEmpty('COUNTRY_CD', 'COUNTRY_CD', 'countryCd');
      mergeIfEmpty('AREA_CODE', 'AREA_CODE', 'areaCode');
      mergeIfEmpty('FIRST_NAME', 'FIRST_NAME', 'firstName');
      mergeIfEmpty('MIDDLE_NAME', 'MIDDLE_NAME', 'middleName');
      mergeIfEmpty('SURNAME', 'SURNAME', 'surname');
      applyContactNamePartsFromFullName();
      normalizeSelectBoundFields();
      
      // Date of Incorporation 설정
      if (contactData.value.DATE_OF_INCORPORATION) {
        dateOfIncorporationValue.value = contactData.value.DATE_OF_INCORPORATION;
      }
    }

    // Officer (Contact) 설정 (contactData 설정 후)
    if (formData.officerNameCd) {
      if (shouldSkipOfficerDbFetchAfterLoadingForm(formData)) {
        skipOfficerContactDbFetchOnce.value = true;
      }
      officerName.value = formData.officerNameCd;
      await nextTick();
    } else if (officerFormDataHasManualProfile(formData)) {
      hydrateContactDataFromFlatOfficerForm(formData);
      officerName.value = MANUAL_OFFICER_SENTINEL;
      await nextTick();
    } else if (
      String(contactData.value.CONTACT_NAME || '').trim() &&
      !String(officerName.value || '').trim()
    ) {
      officerName.value = MANUAL_OFFICER_SENTINEL;
      await nextTick();
    }

    // Appointed Date 설정
    if (formData.appointedDate?.value) {
      appointedDateValue.value = formData.appointedDate.value;
      appointedDate.value = formData.appointedDate.value;
    }

    // Task Flow 설정
    if (formData.taskFlow) {
      taskFlow.value = { ...taskFlow.value, ...formData.taskFlow };
    }

    // Date of Incorporation 설정
    if (contactData.value.DATE_OF_INCORPORATION) {
      dateOfIncorporationValue.value = contactData.value.DATE_OF_INCORPORATION;
    }

    // Alternate Director 정보 복원 (B30024 선택 시에만)
    if (position.value === 'B30024' && formData.contactData?.alternateDirector) {
      const altDir = formData.contactData.alternateDirector;
      alternateDirectorData.value = {
        directorCd: altDir.directorCd || '',
        startDate: altDir.startDate || '',
        endDate: altDir.endDate !== undefined && altDir.endDate !== null ? altDir.endDate : (altDir.hasEndDate ? null : ''), // hasEndDate가 false이면 null, 아니면 endDate 값 또는 빈 문자열
        hasEndDate: altDir.hasEndDate !== undefined ? altDir.hasEndDate : (!!altDir.endDate && altDir.endDate !== null)
      };
      
      // 날짜 필드 설정
      if (altDir.startDate) {
        alternateDirectorStartDateValue.value = altDir.startDate;
      }
      if (altDir.endDate && altDir.endDate !== null) {
        alternateDirectorEndDateValue.value = altDir.endDate;
      } else {
        alternateDirectorEndDateValue.value = '';
      }
      
      // Director 목록이 로드된 후에 directorCd 설정
      if (altDir.directorCd && entity.value) {
        await loadDirectors(entity.value);
        await nextTick();
      }
    }

    console.log('✅ [OfficerAppointmentForm] Form data set successfully');
    
    // watch(officerName)가 완전히 처리될 때까지 약간 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 50));
  } finally {
    // 데이터 복원 모드 종료
    isRestoringData.value = false;
  }
};

// initialData props가 있으면 자동으로 데이터 설정
watch(() => props.initialData, async (newData) => {
  if (newData?.formData) {
    await setFormData(newData.formData);
  }
}, { immediate: true });

// 외부에서 접근 가능하도록 expose
// appointedDate는 dateValue (YYYY-MM-DD 형식)를 사용하여 백엔드로 전달
defineExpose({
  setFormData, // E-Filing 페이지에서 TASK_DATA 설정용
  entity,
  entityName, // Entity 이름 (doc_variable: #CompanyName)
  entityRegNo, // Entity 등록번호 (doc_variable: #RegistrationNo)
  officerName,
  position,
  appointedDate: appointedDateValue, // YYYY-MM-DD 형식의 실제 날짜 값
  taskFlow, // Task Flow 상태
  validateEntity, // Entity 검증 함수
  validateOfficerName, // Officer Name 검증 함수 (Task 생성 시 필수)
  validatePosition, // Position 검증 함수
  validateAppointedDate, // Appointed Date 검증 함수
  validateAll, // 전체 필수 필드 검증 함수
  contactData, // Contact 전체 정보 (저장 시 백엔드로 전달)
  getFormDataWithDocVariables, // doc_variable 정보를 포함한 폼 데이터 반환
  // FORM45 파일명 생성용: Officer Name (실제 이름) + Position Text + Contact 상세 정보
  getOfficerInfo: () => ({
    // 기본 정보
    officerName: contactData.value.CONTACT_NAME || getOfficerDisplayName(officerName.value), // 실제 이름 (contactData에서 먼저 조회, 없으면 officerOptions에서 조회)
    officerCode: isManualOfficerSentinel(officerName.value) ? '' : officerName.value, // 코드 (GUID)
    positionCode: position.value,
    positionText: getPositionText(position.value),
    // Contact 상세 정보 (Basic 섹션)
    contactName: contactData.value.CONTACT_NAME,
    firstName: contactData.value.FIRST_NAME,
    middleName: contactData.value.MIDDLE_NAME,
    surname: contactData.value.SURNAME,
    idTypeCd: contactData.value.ID_TYPE_CD,
    idTypeText: identificationTypeDisplay.value,
    contactRegNo: contactData.value.CONTACT_REG_NO,
    nationalityCd: contactData.value.NATIONALITY_CD,
    nationalityText: nationalityDisplay.value,
    dateOfBirth: contactData.value.DATE_OF_BIRTH,
    // Residential Address 섹션
    addressTypeCd: contactData.value.ADDRESS_TYPE_CD,
    addressTypeText: addressTypeDisplay.value,
    postalCode: contactData.value.POSTAL_CODE,
    blockNo: contactData.value.BLOCK_NO,
    streetName: contactData.value.STREET_NAME,
    buildingName: contactData.value.BUILDING_NAME,
    addrLevel: contactData.value.ADDR_LEVEL,
    addrUnit: contactData.value.ADDR_UNIT,
    foreignAddressLine1: contactData.value.FOREIGN_ADDRESS_LINE_1,
    foreignAddressLine2: contactData.value.FOREIGN_ADDRESS_LINE_2,
    // Contact 섹션
    countryCd: contactData.value.COUNTRY_CD,
    countryText: countryCodeDisplay.value,
    areaCode: contactData.value.AREA_CODE,
    mobileNo: contactData.value.MOBILE_NO,
    contactEmail: contactData.value.CONTACT_EMAIL
  }),
  // Contact 정보 전체 반환 함수 (저장 시 사용)
  getContactData: () => {
    // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
    const addressTypeCd = contactData.value.ADDRESS_TYPE_CD || '';
    const isLocalAddress = addressTypeCd === 'B07001';
    const isForeignAddress = addressTypeCd === 'B07002';
    const altTypeCd = contactAddressTypeCd.value;
    const isAltLocal = altTypeCd === 'B07001';
    const isAltForeign = altTypeCd === 'B07002';
    
    // AUDITOR_YN 처리 (boolean과 'Yes'/'No' 문자열 모두 지원)
    const auditorYnValue = contactData.value.AUDITOR_YN || 'No';
    const auditorYnBoolean = auditorYnValue === 'Yes' ? true : (auditorYnValue === 'No' ? false : undefined);
    
    return {
      // Basic 섹션
      contactCd: isManualOfficerSentinel(officerName.value) ? '' : officerName.value, // CONTACT_CD (GUID)
      contactName: contactData.value.CONTACT_NAME,
      firstName: contactData.value.FIRST_NAME,
      middleName: contactData.value.MIDDLE_NAME,
      surname: contactData.value.SURNAME,
      idTypeCd: contactData.value.ID_TYPE_CD,
      idTypeText: identificationTypeDisplay.value,
      contactRegNo: contactData.value.CONTACT_REG_NO,
      nationalityCd: contactData.value.NATIONALITY_CD,
      nationalityText: nationalityDisplay.value,
      dateOfBirth: contactData.value.DATE_OF_BIRTH,
      categoryCd: contactData.value.CATEGORY_CD,
      // Corporate 필드 (CATEGORY_CD = 'B09001')
      jurisdiction: contactData.value.JURISDICTION || '',
      JURISDICTION: contactData.value.JURISDICTION || '', // snake_case도 함께 저장
      auditorYn: (() => {
        // AUDITOR_YN 처리 (boolean과 'Yes'/'No' 문자열 모두 지원)
        const auditorYnValue = contactData.value.AUDITOR_YN || 'No';
        return auditorYnValue === 'Yes' ? true : (auditorYnValue === 'No' ? false : undefined);
      })(),
      AUDITOR_YN: contactData.value.AUDITOR_YN || 'No', // 'Yes'/'No' 문자열도 함께 저장
      publicAccountingEntities: contactData.value.AUDITOR_YN || 'No', // Create 페이지용 하위 호환성
      dateOfIncorporation: contactData.value.DATE_OF_INCORPORATION || '',
      DATE_OF_INCORPORATION: contactData.value.DATE_OF_INCORPORATION || '', // snake_case도 함께 저장
      incorporationDate: contactData.value.DATE_OF_INCORPORATION || '', // Create 페이지용 하위 호환성
      representativeCd: contactData.value.REPRESENTATIVE_CD || '',
      REPRESENTATIVE_CD: contactData.value.REPRESENTATIVE_CD || '', // snake_case도 함께 저장
      representativeName: contactData.value.REPRESENTATIVE_CD || '', // Create 페이지용 하위 호환성
      // Residential Address 섹션
      addressTypeCd: addressTypeCd,
      addressTypeText: addressTypeDisplay.value,
      postalCode: isLocalAddress ? (contactData.value.POSTAL_CODE || '') : '',
      blockNo: isLocalAddress ? (contactData.value.BLOCK_NO || '') : '',
      streetName: isLocalAddress ? (contactData.value.STREET_NAME || '') : '',
      buildingName: isLocalAddress ? (contactData.value.BUILDING_NAME || '') : '',
      addrLevel: isLocalAddress ? (contactData.value.ADDR_LEVEL || '') : '',
      addrUnit: isLocalAddress ? (contactData.value.ADDR_UNIT || '') : '',
      foreignAddressLine1: isForeignAddress ? (contactData.value.FOREIGN_ADDRESS_LINE_1 || '') : '',
      foreignAddressLine2: isForeignAddress ? (contactData.value.FOREIGN_ADDRESS_LINE_2 || '') : '',
      // Contact Address 섹션 (대체 주소)
      alternateAddressCd: contactData.value.ALTERNATE_ADDRESS_CD || altTypeCd,
      altPostalCode: isAltLocal ? (contactData.value.ALT_POSTAL_CODE || '') : '',
      altBlockNo: isAltLocal ? (contactData.value.ALT_BLOCK_NO || '') : '',
      altStreetName: isAltLocal ? (contactData.value.ALT_STREET_NAME || '') : '',
      altBuildingName: isAltLocal ? (contactData.value.ALT_BUILDING_NAME || '') : '',
      altAddrLevel: isAltLocal ? (contactData.value.ALT_ADDR_LEVEL || '') : '',
      altAddrUnit: isAltLocal ? (contactData.value.ALT_ADDR_UNIT || '') : '',
      altForeignAddressLine1: isAltForeign ? (contactData.value.ALT_FOREIGN_ADDRESS_LINE_1 || '') : '',
      altForeignAddressLine2: isAltForeign ? (contactData.value.ALT_FOREIGN_ADDRESS_LINE_2 || '') : '',
      // Contact 섹션
      countryCd: contactData.value.COUNTRY_CD,
      countryText: countryCodeDisplay.value,
      areaCode: contactData.value.AREA_CODE,
      mobileNo: contactData.value.MOBILE_NO,
      contactEmail: contactData.value.CONTACT_EMAIL,
      // Alternate Director 정보 (B30024 선택 시)
      alternateDirector: isAlternateDirector.value ? {
        directorCd: alternateDirectorData.value.directorCd || '',
        startDate: alternateDirectorData.value.startDate || '',
        endDate: alternateDirectorData.value.hasEndDate && alternateDirectorData.value.endDate !== null && alternateDirectorData.value.endDate !== '' ? alternateDirectorData.value.endDate : null, // hasEndDate가 false이거나 endDate가 null/빈 문자열이면 null
        hasEndDate: alternateDirectorData.value.hasEndDate || false
      } : undefined
    };
  },
  // E-Filing용 JSON 데이터 반환 함수
  getEFilingData
});
</script>

<template>
  <div class="officer-appointment-wrapper">
    <!-- Header with Title and Expand/Collapse buttons -->
    <div class="d-flex justify-space-between align-center mb-3">
      <h3 class="text-h6 font-weight-bold">{{ t('startTask.Officer Appointment Details') || 'Officer Appointment Details' }}</h3>
      <div v-if="entity" class="expand-collapse-actions">
        <span
          class="action-link"
          :class="{ 'active': isAllExpanded }"
          @click="expandAll"
        >
          <v-icon size="small">mdi-chevron-down</v-icon>
          {{ t('startTask.Expand all') || 'Expand all' }}
        </span>
        <span
          class="action-link ml-3"
          :class="{ 'active': !isAllExpanded }"
          @click="collapseAll"
        >
          <v-icon size="small">mdi-chevron-up</v-icon>
          {{ t('startTask.Collapse all') || 'Collapse all' }}
        </span>
      </div>
    </div>

    <!-- Entity Field (아코디언 위에 배치) -->
    <div class="mb-6" style="max-width: 70%;">
      <label class="text-subtitle-2 font-weight-medium mb-2 d-block" data-field="entityName" :doc_variable="'{#CompanyName}'">
        {{ t('startTask.Entity Name') || 'Entity Name' }} <span class="text-error">*</span>
      </label>
      <v-autocomplete
        ref="entityInputRef"
        v-model="entity"
        :items="officerAppointmentStore.entityOptions"
        :placeholder="t('startTask.Search') || 'Search'"
        variant="outlined"
        density="compact"
        :error-messages="entityErrorMessages"
        append-inner-icon="mdi-magnify"
        clearable
        :disabled="props.eFilingMode"
      ></v-autocomplete>
    </div>

    <!-- Accordion Panel (Entity 선택 후 표시) -->
    <v-expansion-panels v-if="entity" v-model="expandedPanels" multiple class="officer-accordion">
      <v-expansion-panel :value="0" class="officer-panel">
        <v-expansion-panel-title class="panel-title">
          <span class="panel-title-text">{{ t('startTask.Position holder') || 'Position holder' }} 1</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="panel-content">
          <!-- Position and Appointed Date Row (Entity 선택 후 표시) -->
          <v-row v-if="entity" class="ma-0 mb-6" style="max-width: 70%;">
            <v-col cols="12" sm="6" class="pa-0 pr-sm-3">
              <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                {{ t('startTask.Position Held of Appointment') || 'Position Held of Appointment' }} <span class="text-error">*</span>
              </label>
              <v-select
                ref="positionInputRef"
                v-model="position"
                :items="positionOptions"
                item-title="title"
                item-value="value"
                :placeholder="t('startTask.Select') || 'Select'"
                variant="outlined"
                density="compact"
                :error-messages="positionErrorMessages"
                clearable
                :loading="isLoadingPositions"
                :disabled="isLoadingPositions || props.eFilingMode"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" class="pa-0 pl-sm-3" data-field="appointedDate" :doc_variable="'{#EffectiveDate},{#AppointmentDate}'">
              <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                {{ t('startTask.Date of Appointment') || 'Date of Appointment' }} <span class="text-error">*</span>
              </label>
              <v-menu
                v-model="appointedDateMenu"
                :close-on-content-click="false"
                location="bottom"
                :disabled="eFilingMode"
              >
                <template v-slot:activator="{ props: menuProps }">
                  <v-text-field
                    ref="appointedDateInputRef"
                    v-bind="menuProps"
                    :model-value="appointedDateDisplay"
                    :placeholder="datePlaceholder"
                    variant="outlined"
                    density="compact"
                    :error-messages="internalValidationErrors.appointedDate ? [internalValidationErrors.appointedDate] : (validationErrors && validationErrors.appointedDate ? [validationErrors.appointedDate] : [])"
                    :rules="appointedDateRules"
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    :disabled="eFilingMode"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="appointedDatePickerValue"
                  v-bind="datePickerProps"
                  @update:model-value="appointedDateMenu = false"
                  :max="todayDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Officer Name Field (Position 선택 후 표시) -->
          <div v-if="entity && position" class="mb-6" style="max-width: 70%;" data-field="officerName" :doc_variable="'{#AppointOfficerName}'">
            <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
              {{ t('startTask.Appoint position holder Name') || 'Appoint position holder Name' }} <span class="text-error">*</span>
            </label>
            <div class="d-flex align-start" style="gap: 12px;">
              <v-autocomplete
                ref="officerNameInputRef"
                v-model="officerName"
                :items="officerOptions"
                :placeholder="position ? (t('startTask.Search') || 'Search') : (t('startTask.Please select Position first') || 'Please select Position first')"
                variant="outlined"
                density="compact"
                :error-messages="validationErrors && validationErrors.officerName ? [validationErrors.officerName] : []"
                :rules="officerNameRules"
                style="flex: 1;"
                :disabled="!position || isLoadingAllOfficers || isLoadingOfficers || eFilingMode"
                :loading="isLoadingAllOfficers || isLoadingOfficers"
                append-inner-icon="mdi-magnify"
                clearable
                hide-details="auto"
                class="officer-name-input"
              ></v-autocomplete>
              <v-btn
                v-if="!eFilingMode"
                color="primary"
                variant="outlined"
                density="compact"
                @click="handleNewContact"
                class="contact-btn"
                style="min-width: 100px; flex-shrink: 0; margin-top: 0;"
              >
                {{ t('startTask.Contact') || 'Contact' }}
              </v-btn>
            </div>
          </div>

          <!-- Contact Details (Officer Name 선택 후 표시) -->
          <template v-if="hasOfficerDetailPanel">
            <!-- 1. Basic 섹션 (개인일 때만 표시) (개인 또는 회사) -->
            <!-- 개인 (Individual) -->
            <v-card v-if="isIndividual" class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
              <v-card-title class="contact-section-title">
                {{ t('contact.personalInformation') || 'Personal information' }}
              </v-card-title>
              <v-card-text>
                <!-- Name (Legal Name) -->
                <v-row>
                  <v-col cols="12" data-field="officerName" :doc_variable="'{#AppointOfficerName}'">
                    <v-text-field
                      :model-value="contactData.CONTACT_NAME"
                      :label="t('contact.Name') || 'Name'"
                      variant="outlined"
                      density="comfortable"
                      readonly
                      hide-details
                      class="readonly-field"
                    />
                  </v-col>
                </v-row>
                <!-- Identification Type, Nationality/Citizenship -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="contactData.ID_TYPE_CD"
                      :items="identificationTypeOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="validationErrors && validationErrors.idType ? [validationErrors.idType] : []"
                      clearable
                    >
                      <template v-slot:label>
                        {{ t('contact.Identification Type') || 'Identification Type' }}<span v-if="eFilingMode" class="text-error"> *</span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="6" data-field="officerNationality" :doc_variable="'{#AppointOfficerNationality}'">
                    <v-select
                      v-model="contactData.NATIONALITY_CD"
                      :items="nationalityOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="validationErrors && validationErrors.nationality ? [validationErrors.nationality] : []"
                      clearable
                    >
                      <template v-slot:label>
                        {{ t('contact.Nationality/Citizenship') || 'Nationality/Citizenship' }}<span v-if="eFilingMode" class="text-error"> *</span>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
                <!-- Identification No., Date of Birth -->
                <v-row>
                  <v-col cols="12" md="6" data-field="officerIdNo" :doc_variable="'{#AppointOfficerIdNo}'">
                    <v-text-field
                      v-model="contactData.CONTACT_REG_NO"
                      :label="t('contact.Identification No') || 'Identification No.'"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="validationErrors && validationErrors.registrationIdNo ? [validationErrors.registrationIdNo] : []"
                    />
                  </v-col>
                  <v-col cols="12" md="6" data-field="officerDateOfBirth" :doc_variable="'{#AppintOfficerDateOfBirth}'">
                    <v-menu
                      v-model="dateOfBirthMenu"
                      :close-on-content-click="false"
                      location="bottom"
                      :disabled="readOnly"
                    >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                          :model-value="dateOfBirthDisplay"
                          :placeholder="datePlaceholder"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          :error-messages="validationErrors && validationErrors.dateOfBirth ? [validationErrors.dateOfBirth] : []"
                          prepend-inner-icon="mdi-calendar"
                        >
                          <template v-slot:label>
                            {{ t('contact.Date of Birth') || 'Date of Birth' }}<span v-if="eFilingMode" class="text-error"> *</span>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dateOfBirthPickerValue"
                        v-bind="datePickerProps"
                        @update:model-value="dateOfBirthMenu = false"
                        :max="todayDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 1-2. Corporate Information 섹션 (회사일 때만 표시) -->
            <v-card v-if="isCorporate" class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
              <v-card-title class="contact-section-title">
                {{ t('contact.Basic') || 'Basic' }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <!-- Left Column -->
                  <v-col cols="12" md="6" data-field="jurisdiction">
                    <v-autocomplete
                      v-model="contactData.JURISDICTION"
                      :items="jurisdictionOptions"
                      item-title="CODE_NAME"
                      item-value="TOTAL_CODE"
                      :label="t('contact.Jurisdiction') || 'Jurisdiction'"
                      :placeholder="t('contact.Please Select') || 'Please Select'"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="internalValidationErrors.jurisdiction ? [internalValidationErrors.jurisdiction] : []"
                      clearable
                    >
                      <template v-slot:label>
                        {{ t('contact.Jurisdiction') || 'Jurisdiction' }}<span v-if="eFilingMode" class="text-error"> *</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  
                  <!-- Right Column -->
                  <v-col cols="12" md="6">
                    <v-radio-group
                      v-model="contactData.AUDITOR_YN"
                      :readonly="readOnly"
                      inline
                    >
                      <template #label>
                        <span>{{ t('contact.Public Accounting Entities') || 'Public Accounting Entities' }}</span>
                      </template>
                      <v-radio :label="t('common.yes') || 'Yes'" value="Yes" />
                      <v-radio :label="t('common.no') || 'No'" value="No" />
                    </v-radio-group>
                  </v-col>
                </v-row>
                
                <v-row>
                  <!-- Left Column -->
                  <v-col cols="12" md="6" data-field="entityName">
                    <v-text-field
                      v-model="contactData.CONTACT_NAME"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="internalValidationErrors.entityName ? [internalValidationErrors.entityName] : []"
                    >
                      <template v-slot:label>
                        {{ t('contact.Entity Name') || 'Entity Name' }}<span v-if="eFilingMode" class="text-error"> *</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  
                  <!-- Right Column -->
                  <v-col cols="12" md="6" data-field="registrationNumber">
                    <v-text-field
                      v-model="contactData.CONTACT_REG_NO"
                      :label="t('contact.Registration Number') || 'Registration Number'"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="internalValidationErrors.registrationIdNo ? [internalValidationErrors.registrationIdNo] : []"
                    >
                      <template v-slot:label>
                        {{ t('contact.Registration Number') || 'Registration Number' }}<span v-if="eFilingMode" class="text-error"> *</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                
                <v-row>
                  <!-- Left Column -->
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateOfIncorporationMenu"
                      :close-on-content-click="false"
                      location="bottom"
                      :disabled="readOnly"
                    >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                          :model-value="dateOfIncorporationDisplay"
                          :placeholder="datePlaceholder"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          prepend-inner-icon="mdi-calendar"
                        >
                          <template v-slot:label>
                            {{ t('contact.Date of Incorporation') || 'Date of Incorporation' }}
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dateOfIncorporationPickerValue"
                        v-bind="datePickerProps"
                        @update:model-value="dateOfIncorporationMenu = false"
                        :max="todayDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  
                  <!-- Right Column -->
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="contactData.REPRESENTATIVE_CD"
                      :items="representativeOptions"
                      item-title="CONTACT_NAME"
                      item-value="CONTACT_CD"
                      :label="t('contact.Representative Name') || 'Representative Name'"
                      :placeholder="t('contact.Please Select') || 'Please Select'"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      clearable
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 회사 (Corporate) -->
            <v-card v-else-if="isCorporate" class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
              <v-card-title class="contact-section-title">
                {{ t('contact.Basic') || 'Basic' }}
              </v-card-title>
              <v-card-text>
                <!-- 첫 번째 row: Jurisdiction, Public Accounting Entities -->
                <v-row>
                  <v-col cols="12" md="6">
                    <!-- Readonly일 때는 텍스트 필드로 표시 -->
                    <v-text-field
                      v-if="readOnly"
                      :model-value="jurisdictionDisplay"
                      :label="t('contact.Jurisdiction') || 'Jurisdiction'"
                      variant="outlined"
                      density="comfortable"
                      readonly
                      hide-details
                      class="readonly-field"
                    />
                    <!-- 수정 가능할 때는 autocomplete 사용 -->
                    <v-autocomplete
                      v-else
                      v-model="contactData.JURISDICTION"
                      :items="jurisdictionOptions"
                      item-title="CODE_NAME"
                      item-value="TOTAL_CODE"
                      :placeholder="t('contact.Please Select') || 'Please Select'"
                      variant="outlined"
                      density="comfortable"
                      data-field="jurisdiction"
                      :error-messages="internalValidationErrors.jurisdiction ? [internalValidationErrors.jurisdiction] : (validationErrors && validationErrors.jurisdiction ? [validationErrors.jurisdiction] : [])"
                      clearable
                    >
                      <template v-slot:label>
                        {{ t('contact.Jurisdiction') || 'Jurisdiction' }}<span v-if="eFilingMode && isCorporate" class="text-error"> *</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-radio-group
                      v-model="contactData.AUDITOR_YN"
                      :readonly="readOnly"
                      inline
                      hide-details
                    >
                      <template #label>
                        <span>{{ t('contact.Public Accounting Entities') || 'Public Accounting Entities' }}</span>
                      </template>
                      <v-radio
                        :label="t('common.yes') || 'Yes'"
                        value="Yes"
                      />
                      <v-radio
                        :label="t('common.no') || 'No'"
                        value="No"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!-- 두 번째 row: Entity Name, Registration Number -->
                <v-row>
                  <v-col cols="12" md="6" data-field="officerName" :doc_variable="'{#AppointOfficerName}'">
                    <v-text-field
                      :model-value="contactData.CONTACT_NAME"
                      variant="outlined"
                      density="comfortable"
                      readonly
                      data-field="entityName"
                      :error-messages="internalValidationErrors.entityName ? [internalValidationErrors.entityName] : (validationErrors && validationErrors.entityName ? [validationErrors.entityName] : [])"
                      class="readonly-field"
                    >
                      <template v-slot:label>
                        {{ t('contact.Entity Name') || 'Entity Name' }}<span v-if="eFilingMode && isCorporate" class="text-error"> *</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6" data-field="officerIdNo" :doc_variable="'{#AppointOfficerIdNo}'">
                    <v-text-field
                      v-model="contactData.CONTACT_REG_NO"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      data-field="registrationIdNo"
                      :error-messages="internalValidationErrors.registrationIdNo ? [internalValidationErrors.registrationIdNo] : (validationErrors && validationErrors.registrationIdNo ? [validationErrors.registrationIdNo] : [])"
                    >
                      <template v-slot:label>
                        {{ t('contact.Registration Number') || 'Registration Number' }}<span v-if="eFilingMode && isCorporate" class="text-error"> *</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <!-- 세 번째 row: Date of Incorporation, Representative Name -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateOfIncorporationMenu"
                      :close-on-content-click="false"
                      location="bottom"
                      :disabled="readOnly"
                    >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                          :model-value="dateOfIncorporationDisplay"
                          :placeholder="datePlaceholder"
                          :label="t('contact.Date of Incorporation') || 'Date of Incorporation'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          :error-messages="validationErrors && validationErrors.dateOfIncorporation ? [validationErrors.dateOfIncorporation] : []"
                          prepend-inner-icon="mdi-calendar"
                        />
                      </template>
                      <v-date-picker
                        v-model="dateOfIncorporationPickerValue"
                        v-bind="datePickerProps"
                        @update:model-value="dateOfIncorporationMenu = false"
                        :max="todayDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="6">
                    <!-- Readonly일 때는 텍스트 필드로 표시 -->
                    <v-text-field
                      v-if="readOnly"
                      :model-value="representativeDisplay"
                      :label="t('contact.Representative Name') || 'Representative Name'"
                      variant="outlined"
                      density="comfortable"
                      readonly
                      hide-details
                      class="readonly-field"
                    />
                    <!-- 수정 가능할 때는 autocomplete 사용 -->
                    <v-autocomplete
                      v-else
                      v-model="contactData.REPRESENTATIVE_CD"
                      :items="representativeOptions"
                      item-title="CONTACT_NAME"
                      item-value="CONTACT_CD"
                      :label="t('contact.Representative Name') || 'Representative Name'"
                      :placeholder="t('contact.Please Select') || 'Please Select'"
                      variant="outlined"
                      density="comfortable"
                      :error-messages="validationErrors && validationErrors.representative ? [validationErrors.representative] : []"
                      clearable
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 2. Residential Address 섹션 -->
            <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }" data-field="officerAddress" :doc_variable="'{#AppointOfficerAddress}'">
              <v-card-title class="contact-section-title">
                {{ t('contact.Residential Address') || 'Residential Address' }}
              </v-card-title>
              <v-card-text>
                <!-- Address Type Radio -->
                <v-row>
                  <v-col cols="12">
                    <v-radio-group
                      v-model="contactData.ADDRESS_TYPE_CD"
                      inline
                      hide-details
                    >
                      <v-radio
                        :label="t('contact.Residential Address') || 'Residential Address'"
                        value="B07001"
                      />
                      <v-radio
                        :label="t('contact.Service Address') || 'Service Address'"
                        value="B07002"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <!-- Local Address (B07001) -->
                <template v-if="contactData.ADDRESS_TYPE_CD === 'B07001'">
                  <v-row>
                    <v-col cols="12" md="4" data-field="residentialPostalCode">
                      <v-text-field
                        v-model="contactData.POSTAL_CODE"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        data-field="postalCode"
                        :error-messages="(internalValidationErrors.residentialPostalCode ? [internalValidationErrors.residentialPostalCode] : (validationErrors && validationErrors.postalCode ? [validationErrors.postalCode] : []).concat(internalValidationErrors.postalCode ? [internalValidationErrors.postalCode] : []))"
                      >
                        <template v-slot:label>
                          {{ t('contact.Postal Code') || 'Postal Code' }}<span v-if="eFilingMode" class="text-error"> *</span>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="contactData.BLOCK_NO"
                        :label="t('contact.Block/House No') || 'Block/House No'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="contactData.STREET_NAME"
                        :label="t('contact.Street Name') || 'Street Name'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="contactData.BUILDING_NAME"
                        :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contactData.ADDR_LEVEL"
                        :label="t('contact.Level') || 'Level'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contactData.ADDR_UNIT"
                        :label="t('contact.Unit') || 'Unit'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </template>

                <!-- Foreign Address (B07002) -->
                <template v-else>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="contactData.FOREIGN_ADDRESS_LINE_1"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        data-field="foreignAddressLine1"
                        :error-messages="internalValidationErrors.residentialForeignAddr1 ? [internalValidationErrors.residentialForeignAddr1] : (validationErrors && validationErrors.foreignAddress1 ? [validationErrors.foreignAddress1] : [])"
                      >
                        <template v-slot:label>
                          {{ t('contact.Foreign Address Line 1') || 'Foreign Address Line 1' }}<span v-if="eFilingMode" class="text-error"> *</span>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="contactData.FOREIGN_ADDRESS_LINE_2"
                        :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </template>
              </v-card-text>
            </v-card>

            <!-- 3. Contact Address 섹션 (대체 주소) - 개인/법인 모두 표시 -->
            <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }" data-field="officerAlternateAddress" :doc_variable="'{#AppointOfficerAltenateAddress}'">
              <v-card-title class="contact-section-title">
                {{ t('contact.Contact Address') || 'Contact Address' }}
              </v-card-title>
              <v-card-text>
                <!-- Address Type Radio (Contact — ALTERNATE_ADDRESS_CD, Residential 과 독립) -->
                <v-row>
                  <v-col cols="12">
                    <v-radio-group
                      v-model="contactData.ALTERNATE_ADDRESS_CD"
                      inline
                      hide-details
                    >
                      <v-radio
                        :label="t('contact.Local Address') || 'Local Address'"
                        value="B07001"
                      />
                      <v-radio
                        :label="t('contact.Foreign Address') || 'Foreign Address'"
                        value="B07002"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <!-- Local Address (ALT - 대체 주소) -->
                <template v-if="isContactAddressLocal">
                  <v-row>
                    <v-col cols="12" md="4" data-field="contactPostalCode">
                      <v-text-field
                        v-model="contactData.ALT_POSTAL_CODE"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        :error-messages="internalValidationErrors.contactPostalCode ? [internalValidationErrors.contactPostalCode] : []"
                        data-field="altPostalCode"
                      >
                        <template v-slot:label>
                          {{ t('contact.Postal Code') || 'Postal Code' }}<span v-if="eFilingMode && isIndividual && !isCorporate" class="text-error"> *</span>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="contactData.ALT_BLOCK_NO"
                        :label="t('contact.Block/House No') || 'Block/House No'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="contactData.ALT_STREET_NAME"
                        :label="t('contact.Street Name') || 'Street Name'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="contactData.ALT_BUILDING_NAME"
                        :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contactData.ALT_ADDR_LEVEL"
                        :label="t('contact.Level') || 'Level'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contactData.ALT_ADDR_UNIT"
                        :label="t('contact.Unit') || 'Unit'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </template>

                <!-- Foreign Address (ALT - 대체 주소) -->
                <template v-else>
                  <v-row>
                    <v-col cols="12" data-field="contactForeignAddr1">
                      <v-text-field
                        v-model="contactData.ALT_FOREIGN_ADDRESS_LINE_1"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        :error-messages="internalValidationErrors.contactForeignAddr1 ? [internalValidationErrors.contactForeignAddr1] : []"
                        data-field="altForeignAddressLine1"
                      >
                        <template v-slot:label>
                          {{ t('contact.Foreign Address Line 1') || 'Foreign Address Line 1' }}<span v-if="eFilingMode && isIndividual && !isCorporate" class="text-error"> *</span>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="contactData.ALT_FOREIGN_ADDRESS_LINE_2"
                        :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </template>
              </v-card-text>
            </v-card>

            <!-- 4. Contact 섹션 -->
            <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
              <v-card-title class="contact-section-title">
                {{ t('contact.Contact') || 'Contact' }}
              </v-card-title>
              <v-card-text>
                <!-- Email -->
                <v-row>
                  <v-col cols="12" data-field="contactEmail" :doc_variable="'{#AppointOffierEmail}'">
                    <v-text-field
                      v-model="contactData.CONTACT_EMAIL"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      data-field="contactEmail"
                      :error-messages="(internalValidationErrors.contactEmail ? [internalValidationErrors.contactEmail] : (validationErrors && validationErrors.officerEmail ? [validationErrors.officerEmail] : []).concat(internalValidationErrors.email ? [internalValidationErrors.email] : []))"
                    >
                      <template v-slot:label>
                        {{ t('contact.Email Address') || 'Email Address' }}<span v-if="eFilingMode && isIndividual && !isCorporate" class="text-error"> *</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <!-- Country Code, Area Code, Mobile No -->
                <v-row data-field="officerContactNo" :doc_variable="'{#AppointOfficerContactNo}'">
                  <v-col cols="12" md="4" data-field="contactMobileNoCountryCode">
                    <v-select
                      v-model="contactData.COUNTRY_CD"
                      :items="countryOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      data-field="countryCd"
                      :error-messages="(internalValidationErrors.contactMobileNoCountryCode ? [internalValidationErrors.contactMobileNoCountryCode] : (validationErrors && validationErrors.mobileCountryCode ? [validationErrors.mobileCountryCode] : [])).concat(internalValidationErrors.mobileNoCountryCode ? [internalValidationErrors.mobileNoCountryCode] : [])"
                      clearable
                    >
                      <template v-slot:label>
                        {{ t('contact.Country Code') || 'Country Code' }}<span v-if="eFilingMode && isIndividual && !isCorporate" class="text-error"> *</span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="contactData.AREA_CODE"
                      :label="t('contact.Area Code') || 'Area Code'"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="4" data-field="contactMobileNo">
                    <v-text-field
                      v-model="contactData.MOBILE_NO"
                      data-field="mobileNo"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :error-messages="(internalValidationErrors.contactMobileNo ? [internalValidationErrors.contactMobileNo] : (validationErrors && validationErrors.mobileNumber ? [validationErrors.mobileNumber] : []).concat(internalValidationErrors.mobileNo ? [internalValidationErrors.mobileNo] : []))"
                    >
                      <template v-slot:label>
                        {{ t('contact.Mobile No') || 'Mobile No' }}<span v-if="eFilingMode && isIndividual && !isCorporate" class="text-error"> *</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Alternate Director 섹션 (B30024 선택 시만 표시) -->
            <v-card v-if="isAlternateDirector && officerName" class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
              <v-card-title class="contact-section-title">
                {{ t('startTask.Director that the position holder is acting on behalf of') || 'Director that the position holder is acting on behalf of' }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="alternateDirectorData.directorCd"
                      :items="directorOptions"
                      item-title="title"
                      item-value="value"
                      :label="t('startTask.List of directors') || 'List of directors'"
                      :placeholder="t('startTask.Select') || 'Select'"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      :loading="isLoadingDirectors"
                      :disabled="isLoadingDirectors"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="alternateDirectorStartDateMenu"
                      :close-on-content-click="false"
                      location="bottom"
                      :disabled="readOnly"
                    >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                          :model-value="alternateDirectorStartDateDisplay"
                          :placeholder="datePlaceholder"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          prepend-inner-icon="mdi-calendar"
                        >
                          <template v-slot:label>
                            {{ t('startTask.Start date') || 'Start date' }}
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="alternateDirectorStartDatePickerValue"
                        v-bind="datePickerProps"
                        @update:model-value="alternateDirectorStartDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <!-- End Date는 Director 아래에 배치 -->
                <v-row v-if="alternateDirectorData.hasEndDate">
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="alternateDirectorEndDateMenu"
                      :close-on-content-click="false"
                      location="bottom"
                      :disabled="readOnly"
                    >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                          :model-value="alternateDirectorEndDateDisplay"
                          :placeholder="datePlaceholder"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          prepend-inner-icon="mdi-calendar"
                        >
                          <template v-slot:label>
                            {{ t('startTask.End date') || 'End date' }}
                          </template>
                          <template v-slot:append-inner v-if="!readOnly">
                            <v-btn
                              icon="mdi-delete"
                              variant="text"
                              color="grey"
                              density="compact"
                              size="small"
                              @click.stop="removeEndDate"
                              style="min-width: 32px; height: 32px; margin-right: -8px;"
                            />
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="alternateDirectorEndDatePickerValue"
                        v-bind="datePickerProps"
                        @update:model-value="alternateDirectorEndDateMenu = false"
                        :min="alternateDirectorStartDateValue"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row v-if="!alternateDirectorData.hasEndDate">
                  <v-col cols="12" md="6">
                    <a
                      v-if="!readOnly"
                      href="#"
                      @click.prevent="addEndDate"
                      class="text-primary text-decoration-none"
                      style="font-size: 14px;"
                    >
                      <v-icon size="small" class="mr-1">mdi-plus</v-icon>
                      {{ t('startTask.Add end date') || 'Add end date' }}
                    </a>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Task Flow Section (Position 선택 후 표시, showTaskFlow가 true일 때만) - 아코디언 밖에 배치 -->
    <TaskFlowSection
      v-if="position && props.showTaskFlow"
      v-model="taskFlow"
      :visible="true"
      :is-all-supported="isAllSupportedFinal"
      :is-create-document-supported="isCreateDocumentSupported"
      :is-request-t-sign-supported="isRequestTSignSupported"
      :is-proceed-with-e-filing-supported="isEFilingSupportedFinal"
      class="mb-6 mt-4"
      style="max-width: 70%;"
    />

    <!-- Add Position Holder Button (추후 다건 입력 구현 시 활성화)
    <div class="add-position-holder-btn mt-4" @click="() => {}">
      <v-icon color="primary" size="small">mdi-plus</v-icon>
      <span>{{ t('startTask.Add position holder') || 'Add position holder' }}</span>
    </div>
    -->
  </div>

  <!-- Contact List Modal -->
  <ContactListPage
    :is-modal="true"
    v-model="showContactListModal"
    :initial-search="contactListInitialSearch"
    :on-select="handleContactSelect"
    :exclude-contact-cds="excludedOfficers.map(o => o.contactCd)"
  />

  <!-- Contact 상세 화면 모달 (선택된 Contact의 상세 정보) -->
  <v-dialog
    v-model="showContactDetailModal"
    max-width="1400px"
    width="95%"
    persistent
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">{{ t('startTask.Contact Details') || 'Contact Details' }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="showContactDetailModal = false"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 80vh; overflow-y: auto;">
        <ContactDetailPage
          v-if="officerName && !isManualOfficerSentinel(officerName)"
          :contact-id="officerName"
          :is-modal="true"
          :on-select="handleContactDetailSelect"
          @select="handleContactDetailSelect"
          @updated="handleContactUpdated"
          @back="() => { if (!isContactUpdating) showContactDetailModal = false; }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
/* 폰트 패밀리 - Inter */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.officer-appointment-wrapper {
  margin-bottom: 24px;
}

/* Expand/Collapse 액션 링크 */
.expand-collapse-actions {
  display: flex;
  align-items: center;

  .action-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    color: #777E85;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #3C4650;
    }

    &.active {
      color: #3C4650;
      font-weight: 500;
    }
  }
}

/* 아코디언 스타일 */
.officer-accordion {
  :deep(.v-expansion-panel) {
    border: 1px solid #e0e0e0;
    border-radius: 4px !important;
    margin-bottom: 0;

    &::before {
      box-shadow: none !important;
    }
  }

  :deep(.v-expansion-panel--active) {
    .v-expansion-panel-title {
      background-color: #1B84FF;
      color: white;
      border-radius: 4px 4px 0 0;

      .panel-title-text {
        color: white;
      }

      .v-expansion-panel-title__icon {
        color: white;
      }
    }
  }

  :deep(.v-expansion-panel-title) {
    min-height: 48px;
    padding: 12px 16px;
    background-color: #fff;

    .panel-title-text {
      font-size: 16px;
      font-weight: 600;
      color: #3C4650;
    }
  }

  :deep(.v-expansion-panel--active > .v-expansion-panel-title:hover) {
    background-color: #1B84FF;
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 24px;
  }
}

/* Add position holder 버튼 */
.add-position-holder-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border: 2px dashed #1B84FF;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s;

  span {
    font-size: 14px;
    font-weight: 500;
    color: #1B84FF;
  }

  &:hover {
    background-color: rgba(27, 132, 255, 0.05);
    border-color: #0066CC;

    span {
      color: #0066CC;
    }
  }
}

/* 레이블 - 14px */
:deep(.v-label),
:deep(.v-field-label) {
  font-size: 14px !important;
  color: #3C4650;
}

/* 입력 필드 - 14px */
:deep(.v-field__input) {
  font-size: 14px !important;
  color: #3C4650;
}

/* placeholder - 14px #777E85 */
:deep(.v-field__input::placeholder),
:deep(input::placeholder) {
  font-size: 14px !important;
  color: #777E85 !important;
}

/* 버튼 - 14px */
:deep(.v-btn) {
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none;
}

/* Primary 버튼 */
:deep(.v-btn--color-primary) {
  background-color: #1B84FF !important;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

/* Section Title 스타일 */
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #3C4650;
  margin-bottom: 16px;
}

/* 클릭 가능한 필드 스타일 */
.clickable-field {
  cursor: pointer;

  :deep(.v-field) {
    cursor: pointer;
  }

  &:hover :deep(.v-field) {
    background-color: rgba(27, 132, 255, 0.05);
  }
}

/* Contact 섹션 카드 스타일 */
.contact-section-card {
  background-color: white !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  &:hover {
    background-color: white !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
    border-color: rgba(0, 0, 0, 0.12) !important;
  }

  /* v-card 내부 overlay 제거 */
  :deep(.v-card__overlay) {
    display: none !important;
    opacity: 0 !important;
    background-color: transparent !important;
  }
}

.contact-section-title {
  font-size: 18px;
  font-weight: 700;
  color: #3C4650;
  padding: 16px 24px;
}

.contact-section-card :deep(.v-card-text) {
  padding: 0 24px 24px !important;
}

/* Readonly 필드 스타일 - disabled 상태 유지, 배경색 흰색, hover 효과 제거 */
.readonly-field {
  pointer-events: none;
}

.readonly-field :deep(.v-field) {
  background-color: white !important;
  opacity: 1 !important;
}

.readonly-field :deep(.v-field:hover) {
  background-color: white !important;
}

.readonly-field :deep(.v-field__input) {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
  background-color: white !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
  background-color: white !important;
}

.readonly-field :deep(.v-field--variant-outlined:hover) {
  background-color: white !important;
}

.readonly-field :deep(.v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

.readonly-field :deep(input) {
  cursor: default !important;
  background-color: white !important;
}

/* Contact 섹션 카드 내부 필드 hover 제거 */
.contact-section-card :deep(.v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

.contact-section-card :deep(.v-field:hover .v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

/* Radio group readonly 스타일 */
.contact-section-card :deep(.v-radio-group) {
  pointer-events: auto !important;
}

.contact-section-card :deep(.v-selection-control--disabled) {
  opacity: 1 !important;
}

.contact-section-card :deep(.v-label--clickable) {
  cursor: default !important;
  opacity: 1 !important;
}

/* ReadOnly 상태에서 Contact 카드 클릭 비활성화 */
.contact-section-card-readonly {
  cursor: default !important;
  pointer-events: none;
}

/* ReadOnly 상태에서도 라디오 그룹은 클릭 가능하도록 */
.contact-section-card-readonly :deep(.v-radio-group) {
  pointer-events: auto !important;
}

.contact-section-card-readonly:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}

/* Officer Name Input과 Contact 버튼 높이 동일하게 맞추기 */
.officer-name-input :deep(.v-field) {
  min-height: 40px;
}

.contact-btn {
  height: 40px !important;
  min-height: 40px !important;
}
</style>
